/**
 * THE CLAIMS RULES, AS DATA (ops #32). The one file in ops allowed to spell the words the business
 * must never say, because a rule has to name the thing it forbids: `api/test/house-style.test.ts`
 * exempts it by name, as `../faithfully-kept-business/.claude/hooks/claims-guard.mjs` allowlists
 * `docs/what-we-cannot-say.md` for the same reason. Nothing else is exempt, so the functions that run
 * these rules (`claims.ts`) carry no forbidden word and are scanned like any file.
 *
 * THE PRINCIPLE (the rule table of 2026-10-16). The server refuses only what it can recognise with
 * high precision, anchored on the nouns and phrases `what-we-cannot-say.md` forbids. A generic word
 * never refuses on the server; it may warn on the screen. A wrong refusal costs a client her notice of
 * a moved visit, and under binding rule 7 the move itself; a missed claim costs a reworded sentence a
 * person can still catch in amber before Send. So when a rule cannot be both precise and complete, it
 * warns.
 *
 * FOUR LISTS AND A STAMP.
 *
 *   `BUSINESS_RULES` is the business's own hook, its `PATTERNS` copied row for row, each as the regex
 *   source, its flags and the true thing to say instead: seventeen in English and, since business
 *   9ca990c (ops #98), sixteen in Spanish under `es-` ids, which the office's Spanish versions meet.
 *   The block between the two `claims-sync` markers below is written by `scripts/claims-sync.mjs`
 *   and by nothing else: run `npm run claims:sync`, read the diff, and update the hash and the
 *   digest pinned in `api/test/claims.test.ts`. A loosening wanted on ops's side is never made in the copy; it is a row
 *   of `COPIED_RULE_EXCEPTIONS`, or a skip in `claims.ts`, where a test names it.
 *
 *   `BUSINESS_RULES_SOURCE` stamps the copy: where it came from, the sha256 of the hook as git holds
 *   it (LF endings, whatever a checkout writes), the digest of the copied rows themselves, and the day
 *   it was taken. `npm run claims:check`, in the gate, fails when the hook and the copy disagree.
 *
 *   `COPIED_RULE_EXCEPTIONS` narrows a copied rule at runtime, for a shape that names somebody else.
 *
 *   `OPS_RULES` are ops's own, for what the hook does not catch in a text to a client: a price
 *   (`price_not_allowed`, binding rule 3 and the seam rule that prices are never invented or edited
 *   here), and the claims the words the office types reach for (`claim_not_allowed`, binding rule 8).
 *   Each is a refusal on the server, made of anchors, each anchor matched on its own.
 *
 *   `OPS_WARNINGS` are the screen's amber, never a refusal: the bare words the server leaves alone,
 *   where the screen can afford to be eager. The server never reads them.
 *
 * The copied block is string literals only, never `String.raw`, so the script can write it back byte
 * for byte; ops's own rules are string literals and the shared pieces spliced between them. Every rule
 * is matched case-insensitively on the text taken to NFKC, after the one subtraction `claims.ts`
 * makes. A character a pattern needs beyond ASCII is written as its escape, never typed.
 */

/** The two codes a refusal answers `422` with. */
export type ClaimsCode = 'claim_not_allowed' | 'price_not_allowed';

/** One rule: its id, the regex source and flags, and one sentence telling the office what to change. */
export interface ClaimsRule {
  readonly id: string;
  readonly source: string;
  readonly flags: string;
  readonly say: string;
}

/** Where the copy came from and when, and the two hashes that keep it honest. */
export interface ClaimsRulesSource {
  /** The hook, inside the business repository. */
  readonly path: string;
  /** The sha256 of the hook's bytes with LF endings. */
  readonly sha256: string;
  /** The sha256 of the canonical JSON of the copied rows, `digestOf` in the script. */
  readonly digest: string;
  /** The day the copy was taken, YYYY-MM-DD. */
  readonly takenOn: string;
}

// claims-sync: begin
export const BUSINESS_RULES_SOURCE: ClaimsRulesSource = {
  path: '.claude/hooks/claims-guard.mjs',
  sha256: '538c1ea4f162b51998a35529d0f85939201ba9fbd6c610082522b375e0067444',
  digest: 'c0e795a154504c891083f81669d32cdd73fe214f60b4957e8cc8634bb0b1781f',
  takenOn: '2026-10-25',
};

export const BUSINESS_RULES: readonly ClaimsRule[] = [
  {
    id: 'insured',
    source:
      '\\b(fully\\s+)?insured\\b|\\binsurance\\s+(cover|covered)\\b|\\$\\s?\\d+(\\.\\d+)?\\s?(M|million)\\b|\\$\\s?\\d{1,3}(,\\d{3}){2,}\\b|\\bcoverage\\s+(up\\s+to|limit)\\b',
    flags: 'i',
    say: 'No policy is bound. Say: "We carry no general liability cover yet, and I would rather tell you now than after something happens."',
  },
  {
    id: 'bonded',
    source: '\\bbonded\\b|\\bdishonesty\\s+bond\\b|\\bfidelity\\s+bond\\b',
    flags: 'i',
    say: 'No bond is issued. Name it plainly if asked, and do not raise it otherwise.',
  },
  {
    id: 'licensed',
    source: '\\blicen[sc]ed\\b|(?<!\\bno\\s)\\bstate\\s+licen[sc]e',
    flags: 'i',
    say: 'Florida requires NO licence for residential cleaning, so this can never become true. Say: "There is no state licence for house cleaning in Florida."',
  },
  {
    id: 'eando',
    source: '\\bE\\s?&\\s?O\\b|\\berrors\\s+and\\s+omissions\\b',
    flags: 'i',
    say: 'Not in force. Say nothing about it.',
  },
  {
    id: 'accreditation',
    source: '\\bNHWA\\b|\\baccredited\\b|\\bcertified\\b(?!\\s+(mail|letter|copy|check|funds))',
    flags: 'i',
    say: 'No membership or certification exists.',
  },
  {
    id: 'background-checked',
    source: '\\bbackground[\\s-]?(checked|screened)\\b|\\bvetted\\b',
    flags: 'i',
    say: 'No checks have been run. Say: "Nobody has been through a background check yet, because we are new."',
  },
  {
    id: 'workers-comp',
    source: "\\bworkers?'?\\s?comp\\b|\\bworker's\\s+comp\\b|\\bcovered\\s+crew\\b",
    flags: 'i',
    say: 'No policy exists.',
  },
  {
    id: 'our-staff',
    source: '\\bour\\s+(staff|employees|team\\s+members)\\b|\\bW-?2\\b',
    flags: 'i',
    say: 'Two lanes were decided on 2026-10-22, but nobody is on payroll yet, so these phrases are not true. Say "the cleaner assigned to your home".',
  },
  {
    id: 'same-cleaner',
    source: '\\bsame\\s+cleaner\\s+every\\s+(visit|time)\\b|\\bnever\\s+a\\s+no[\\s-]?show\\b',
    flags: 'i',
    say: 'Nobody is hired long enough for that promise to survive its first sick day.',
  },
  {
    id: 'tenure',
    source:
      '\\bsince\\s+(19|20)\\d{2}\\b|\\b(\\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|fifteen|twenty|thirty|forty|fifty|a\\s+hundred|hundreds|thousands|dozens)\\+?\\s+years?\\s+(of\\s+)?(experience|in\\s+business)\\b|\\bestablished\\s+(in\\s+)?(19|20)\\d{2}\\b',
    flags: 'i',
    say: 'The business has not traded. Say: "We are new. That is why everything is published."',
  },
  {
    id: 'scale',
    source:
      '\\btrusted\\s+by\\s+(\\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|fifteen|twenty|thirty|forty|fifty|a\\s+hundred|hundreds|thousands|dozens)\\b|(?<!\\bno\\s)\\b(\\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|fifteen|twenty|thirty|forty|fifty|a\\s+hundred|hundreds|thousands|dozens)\\+?\\s+(homes|families|clients)\\s+(cleaned|served)\\b|\\b(\\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|fifteen|twenty|thirty|forty|fifty|a\\s+hundred|hundreds|thousands|dozens)\\+?\\s+(happy|satisfied|loyal|repeat)\\s+(clients|customers|families|homeowners)\\b',
    flags: 'i',
    say: 'Zero jobs are done.',
  },
  {
    id: 'reviews',
    source:
      '\\b(\\d+(\\.\\d)?|one|two|three|four|five)[\\s-]*stars?\\s+(service|rating|ratings|reviews?|cleaning|cleaners?|company|business|clean|experience|results?|work)\\b|\\brated\\s+(\\d+(\\.\\d)?|one|two|three|four|five)[\\s-]*stars?\\b|\\b\\d(\\.\\d)?\\s+stars\\b|\\b(two|three|four|five)\\s+stars\\b|\\b\\d+(\\.\\d)?\\s*\\/\\s*5\\b|\\b\\d+\\+?\\s+(5[\\s-]star\\s+)?reviews?\\b|\\b(two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|fifteen|twenty|thirty|forty|fifty|a\\s+hundred|hundreds|thousands|dozens)\\+?\\s+(5[\\s-]star\\s+)?reviews\\b',
    flags: 'i',
    say: 'Zero reviews exist.',
  },
  {
    id: 'ch475',
    source:
      '\\bproperty\\s+management\\b|\\bmanage\\s+your\\s+property\\b|\\brental\\s+management\\b|\\btenant\\s+placement\\b|\\bleasing\\b|\\brent\\s+your\\s+home\\b',
    flags: 'i',
    say: 'F.S. 475.42 makes unlicensed brokerage a first-degree misdemeanour. Safe verbs: inspect, check, monitor, report, coordinate access, notify. "Cleaning FOR property managers" is fine.',
  },
  {
    id: 'germs',
    source:
      '\\bsaniti[sz](e|es|ed|ing|er|ers)\\b|\\bdisinfect(s|ed|ing|ant|ants)?\\b|\\bkills?\\s+(germs|bacteria|mold|mould|viruses)\\b|\\bremov(e|es|ed|ing)\\s+(the\\s+)?(visible\\s+)?mou?ld\\b|\\bmou?ld\\s+removal\\b|\\bgerm[\\s-]?free\\b|\\ballergen[\\s-]?free\\b|\\bremoves?\\s+allergens\\b|\\bantibacterial\\b|\\bantimicrobial\\b|\\bhygienic\\b|\\b99(\\.9+)?\\s?(%|percent)',
    flags: 'i',
    say: 'A cleaning service has no test evidence for a germ or health claim, and steam devices count as antimicrobial devices to the EPA. Say what can be seen: "every grout line scrubbed", "pH-neutral cleaner on grout".',
  },
  {
    id: 'paraphrase',
    source:
      '\\bpeace\\s+of\\s+mind\\b|\\brest\\s+easy\\b|\\bworry[\\s-]?free\\b|\\bsafeguard(s|ed|ing)?\\b',
    flags: 'i',
    say: 'These make the credential claim without its noun. Rewrite the sentence around what is true and checkable.',
  },
  {
    id: 'sms-review-request',
    source:
      '\\b(text|sms)\\b[^.!?\\n]{0,50}\\b(review|rating|testimonial)\\b|\\b(review|rating|testimonial)\\b[^.!?\\n]{0,50}\\b(by|via|through|over|in)\\s+(a\\s+|an\\s+)?(text|sms)\\b',
    flags: 'i',
    say: 'Review requests are never sent by message, registered or not. A message carrying any promotional purpose is treated as marketing under a dual-purpose analysis, which moves it into the written-consent bucket and inside the Florida act. Ask in person on the day of the clean, and by email. The business is choosing the slower path deliberately; see docs/phone-messaging-and-ai.md.',
  },
  {
    id: 'sms-marketing',
    source:
      '\\b(promotional|marketing)\\s+(text|sms)\\b|\\b(text|sms)\\b[^.!?\\n]{0,50}\\b(discount|coupon|special offer|promotion|our specials)\\b',
    flags: 'i',
    say: 'Marketing by message is forbidden regardless of registration. Only operational messages to a client who booked and supplied her number for that purpose are permitted: confirmations, schedule changes, arrival notices. Consent covers the transaction it was given for and nothing else.',
  },
  {
    id: 'es-insured',
    source: '\\basegurad[oa]s?\\b',
    flags: 'iu',
    say: 'The Spanish of "insured": no policy is bound.',
  },
  {
    id: 'es-bonded',
    source: '\\bafianzad[oa]s?\\b',
    flags: 'iu',
    say: 'The Spanish of "bonded": no bond is issued.',
  },
  {
    id: 'es-licensed',
    source:
      '\\bcon\\s+licencia\\b|\\blicenciad[oa]s?\\b|(?<!\\b(no\\s+(hay|existe)|ninguna)\\s+)\\blicencia\\s+estatal\\b',
    flags: 'iu',
    say: 'The Spanish of "licensed": Florida has no licence for house cleaning. "No hay licencia estatal para la limpieza de casas en Florida" passes.',
  },
  {
    id: 'es-certified',
    source: '\\bcertificad[oa]s?\\b|\\bacreditad[oa]s?\\b',
    flags: 'iu',
    say: 'The Spanish of "certified" or "accredited": none is held. A credited payment is "pagado".',
  },
  {
    id: 'es-vetted',
    source: '\\bverificad[oa]s?\\b',
    flags: 'iu',
    say: 'The Spanish of "vetted": no checks have been run. A sign-in that went through is "confirmado".',
  },
  {
    id: 'es-trusted',
    source: '\\bde\\s+confianza\\b|\\bconfiables?\\b',
    flags: 'iu',
    say: 'The Spanish of "trusted": a paraphrase the document forbids.',
  },
  {
    id: 'es-protected',
    source: '\\bprotegid[oa]s?\\b',
    flags: 'iu',
    say: 'The Spanish of "protected": a paraphrase the document forbids. A locked home is "cerrada con llave".',
  },
  {
    id: 'es-peace-of-mind',
    source: '\\btranquilidad\\b|\\bsin\\s+preocupaciones\\b',
    flags: 'iu',
    say: 'The Spanish of "peace of mind" or "worry-free".',
  },
  {
    id: 'es-our-staff',
    source:
      '\\bnuestro\\s+personal\\b|\\bnuestr[oa]s\\s+emplead[oa]s\\b|\\bmiembros\\s+de\\s+nuestro\\s+equipo\\b',
    flags: 'iu',
    say: 'The Spanish of "our staff" or "our employees": nobody is on payroll yet. Say "la persona asignada a su casa".',
  },
  {
    id: 'es-germs',
    source:
      '\\bdesinfec\\p{L}*|\\bsanitiz\\p{L}*|\\bantimicrobian[oa]s?\\b|\\bantibacterian[oa]s?\\b|\\b(elimin|mat)\\p{L}*\\s+((los|las|de)\\s+)?(g\u00e9rmenes|bacterias|al\u00e9rgenos)\\b|\\b(elimin|quit|mat|remo[vc]|retir)\\p{L}*\\s+((el|del|de|todo\\s+el)\\s+)?moho\\b|\\bsin\\s+moho\\b|\\blibres?\\s+de\\s+(g\u00e9rmenes|bacterias|al\u00e9rgenos|moho)\\b|(?<!\\bpapel\\s+)\\bhigi\u00e9nic[oa]s?\\b|\\b99([.,]\\d+)?\\s?por\\s+ciento\\b',
    flags: 'iu',
    say: 'A germ, health or mould claim in Spanish. Say what can be seen.',
  },
  {
    id: 'es-tenure',
    source:
      '(?<!\\p{L})(?:(a\u00f1os|d\u00e9cadas?)\\s+(de\\s+experiencia|en\\s+el\\s+(negocio|mercado|sector|rubro))|desde\\s+(el\\s+(a\u00f1o\\s+)?)?(19|20)\\d{2}(?!\\d)|establecid[oa]s?\\s+en|fundad[oa]s?\\s+en\\s+(el\\s+(a\u00f1o\\s+)?)?(19|20)\\d{2}(?!\\d))(?!\\p{L})',
    flags: 'iu',
    say: 'Tenure in Spanish: the business is new. What a rule says is "lo que indica", not "lo establecido en".',
  },
  {
    id: 'es-scale',
    source:
      '(?<!\\p{L})(?:(\\d+\\+?|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez|once|doce|trece|catorce|quince|dieci\\p{L}+|veinte|veinti\\p{L}+|treinta|cuarenta|cincuenta|sesenta|setenta|ochenta|noventa|cien|cientos?|(dos|tres|cuatro|seis|sete|ocho|nove)cient[oa]s|quinient[oa]s|mil|miles|docenas|decenas|much[oa]s|vari[oa]s|innumerables)\\s+(de\\s+)?(familias|casas|hogares|viviendas|clientes|clientas)\\s+(atendid|limpiad|servid)[oa]s|confianza\\s+de\\s+(m\u00e1s\\s+de\\s+)?(\\d+\\+?|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez|once|doce|trece|catorce|quince|dieci\\p{L}+|veinte|veinti\\p{L}+|treinta|cuarenta|cincuenta|sesenta|setenta|ochenta|noventa|cien|cientos?|(dos|tres|cuatro|seis|sete|ocho|nove)cient[oa]s|quinient[oa]s|mil|miles|docenas|decenas|much[oa]s|vari[oa]s|innumerables)\\s+(de\\s+)?(familias|casas|hogares|viviendas|clientes|clientas)|(\\d+\\+?|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez|once|doce|trece|catorce|quince|dieci\\p{L}+|veinte|veinti\\p{L}+|treinta|cuarenta|cincuenta|sesenta|setenta|ochenta|noventa|cien|cientos?|(dos|tres|cuatro|seis|sete|ocho|nove)cient[oa]s|quinient[oa]s|mil|miles|docenas|decenas|much[oa]s|vari[oa]s|innumerables)\\s+(de\\s+)?((clientes|clientas|familias|hogares|propietari[oa]s|due\u00f1[oa]s)\\s+(muy\\s+)?(satisfech[oa]s|felices|content[oa]s|leales|fieles|recurrentes|habituales)|(satisfech[oa]s|felices|content[oa]s|leales|fieles|recurrentes|habituales)\\s+(clientes|clientas|familias|hogares|propietari[oa]s|due\u00f1[oa]s)))(?!\\p{L})',
    flags: 'iu',
    say: 'Scale in Spanish: zero jobs are done.',
  },
  {
    id: 'es-reviews',
    source:
      '(?<!\\p{L})(?:(\\d+([.,]\\d+)?|dos|tres|cuatro|cinco)[\\s-]+estrellas?|(calificaci|valoraci|puntuaci)\\p{L}*\\s+((media|promedio)\\s+)?(de\\s+)?\\d|(\\d+\\+?|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez|once|doce|trece|catorce|quince|dieci\\p{L}+|veinte|veinti\\p{L}+|treinta|cuarenta|cincuenta|sesenta|setenta|ochenta|noventa|cien|cientos?|(dos|tres|cuatro|seis|sete|ocho|nove)cient[oa]s|quinient[oa]s|mil|miles|docenas|decenas|much[oa]s|vari[oa]s|innumerables)\\s+(de\\s+)?(rese\u00f1|opini|valoraci)\\p{L}*)(?!\\p{L})',
    flags: 'iu',
    say: 'Reviews or stars in Spanish: zero reviews exist.',
  },
  {
    id: 'es-coverage-limit',
    source:
      '(?<!\\p{L})(?:cobertura\\s+(de\\s+)?hasta|l[i\u00ed]mites?\\s+de\\s+(la\\s+)?(cobertura|responsabilidad)|mill[o\u00f3]n|millones)(?!\\p{L})|\\$\\s?\\d{1,3}([.,\\s]\\d{3}){2,}',
    flags: 'iu',
    say: 'A coverage limit in Spanish: no policy is bound.',
  },
  {
    id: 'es-property',
    source:
      '(?<!\\p{L})(?:(administraci[o\u00f3]n|gesti[o\u00f3]n)\\s+de\\s+(propiedades|inmuebles|alquileres|rentas)|(administr|gestion)(o|a|amos|an|aremos)\\s+(su|sus|tu|tus)\\s+(casa|hogar|vivienda|propiedad|inmueble|condominio)\\p{L}*|(alquil|rent|arriend)(o|a|amos|an|aremos|ar)\\s+(su|sus|tu|tus)\\s+(casa|hogar|vivienda|propiedad|inmueble|condominio)\\p{L}*|(colocaci[o\u00f3]n|b[u\u00fa]squeda|selecci[o\u00f3]n)\\s+de\\s+inquilinos|arrendamientos?)(?!\\p{L})',
    flags: 'iu',
    say: 'Property management in Spanish: F.S. 475.42. "Limpieza para administradores de propiedades" names an audience and passes.',
  },
  {
    id: 'es-trust-us',
    source:
      '(?<!\\p{L})(?:(puede|pueden|puedes|pod\u00e9s|podr\u00e1|podr\u00e1n|podr\u00e1s|podr\u00eda|podr\u00edan)\\s+confiar\\p{L}*|conf[i\u00ed](e|en|a|an)\\s+en\\s+nosotros|en\\s+quien(es)?\\s+confiar)(?!\\p{L})',
    flags: 'iu',
    say: '"You can trust us" in Spanish: a paraphrase the document forbids. "Gracias por confiar en nosotros" passes.',
  },
];
// claims-sync: end

/**
 * NARROWING A COPIED RULE, never in the copy. A match of a copied rule is dropped when its words are
 * `match` (whole) and the text before it ends with `before` and the text after it starts with `after`,
 * each part that is given, case-insensitively. The copy is never edited, the sync script never touches
 * this table, and the pinned digest stays what it is. Every row is proved in
 * `api/test/claims-corpus.test.ts` on one sentence it spares and one it does not.
 *
 *   `licensed`: a licence named of somebody else's trade ("call a licensed plumber", "a licensed AC
 *   company"). "We are licensed" and "licensed and insured" stay refused.
 *   `ch475`: a third party named, her property management company or team and the leasing office or
 *   center of her building. "We offer property management", "our property management", "we do
 *   leasing" stay refused.
 *   `tenure`: a thing's state since a year, said in the negative of something done to it ("the gate list
 *   hasn't been updated since 2024"). "We have held your key since 2025" stays refused, as does any
 *   sentence with "we" or "I" as its subject.
 *   `scale`: a cleaner's own day, a single figure before hers ("2 homes cleaned before yours").
 */
export interface CopiedRuleException {
  /** The words the copied rule matched, whole; any match of the rule where absent. */
  readonly match?: string;
  /** What the text before the match ends with, anchored with `$`. */
  readonly before?: string;
  /** What the text after the match starts with, anchored with `^`. */
  readonly after?: string;
}

export const COPIED_RULE_EXCEPTIONS: Readonly<Record<string, readonly CopiedRuleException[]>> = {
  licensed: [
    {
      after:
        '^\\s+(?:plumber|electrician|contractor|handyman|roofer|hvac|technician|tech|pest\\s+control|exterminator|pool\\s+(?:guy|tech|company|service)|locksmith|mover|movers|painter|installer|inspector|appraiser|realtor|agent|vet|therapist|nurse|cpa|attorney|lawyer|(?:ac|a\\/c|air[\\s-]conditioning|plumbing|electrical|roofing|appliance|septic|irrigation|garage[\\s-]door|tree)\\s+(?:company|companies|business|tech|techs|technicians?|contractors?|guy|guys|pros?|service|repair(?:man|men|person)?|person|people|crew))s?\\b',
    },
  ],
  ch475: [
    {
      match: 'property\\s+management',
      before:
        "\\b(?:your|her|his|their|the|a|my)\\s+(?:building['\\u2019]?s?\\s+|condo['\\u2019]?s?\\s+|hoa['\\u2019]?s?\\s+|association['\\u2019]?s?\\s+|complex['\\u2019]?s?\\s+)?$",
      after:
        '^\\s+(?:company|companies|firm|firms|office|offices|group|people|folks|team|staff|desk|department|dept|contact|rep)\\b',
    },
    {
      match: 'leasing',
      after:
        '^\\s+(?:office|offices|center|centre|agent|agents|desk|manager|managers|company|consultant|team|staff|department|dept)\\b',
    },
  ],
  tenure: [
    {
      match: 'since\\s+(?:19|20)\\d{2}',
      before:
        "\\b(?!(?:we|i)\\b)[a-z]+\\s+(?:has|have|had)(?:n['\\u2019]t|\\s+not)\\s+been\\s+(?:[a-z]+\\s+){1,2}$",
    },
  ],
  scale: [
    {
      match: '[1-9]\\s+(?:homes|families|clients)\\s+(?:cleaned|served)',
      after: '^\\s+(?:before|ahead\\s+of|after|today|this\\s+(?:morning|afternoon|week))\\b',
    },
  ],
};

/**
 * THE PIECES SEVERAL RULES SHARE, written once and spliced into the sources, so every rule that reads
 * a figure reads it the same way. Each is a regex source, as the rules are.
 */

/** A currency mark: the dollar, the euro, the pound. */
const MARK = '(?:\\$|\\u20ac|\\u00a3)';

/** A figure as typed: digits, commas between them, any decimal part ("4242, $212" is two). */
const NUM = '\\d(?:[\\d,]*\\d)?(?:\\.\\d+)?';

/** A figure of 13 or more, as the screen weighs one: 10, 11 and 12 are hours, and her card is hers. */
const FIGURE = '(?<!ending\\s)(?:\\d{1,3}(?:,\\d{3})+|[1-9]\\d{2,}|1[3-9]|[2-9]\\d)';

/** The months, spelled or cut short. */
export const MONTH_NAMES =
  'Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|June?|July?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?';

/**
 * The things a cleaner counts after a figure, in a house and on a day: "3 bedrooms", "14 blinds", "3
 * houses before yours". A figure one of these follows is a count, never a price or a rating.
 */
const COUNT_WORDS = [
  'rooms?',
  'bedrooms?',
  'bathrooms?',
  'baths?',
  'beds?',
  'visits?',
  'cleans?',
  'cleanings?',
  'people',
  'persons?',
  'cleaners?',
  'windows?',
  'doors?',
  'loads?',
  'bags?',
  'bottles?',
  'boxes',
  'floors?',
  'stor(?:y|ies)',
  'pets?',
  'dogs?',
  'cats?',
  'kids?',
  'items?',
  'pieces?',
  'towels?',
  'sheets?',
  'rugs?',
  'chairs?',
  'stairs?',
  'flights?',
  'houses?',
  'homes?',
  'stops?',
  'jobs?',
  'blinds',
  'ovens?',
  'fridges?',
  'sinks?',
  'toilets?',
  'showers?',
  'tubs?',
  'closets?',
  'cabinets?',
  'drawers?',
  'shelves',
  'mirrors?',
  'fans?',
  'screens?',
].join('|');

/** The words after a figure that make it a count, a time, a share or a measure. */
const UNIT_WORDS = [
  'percent',
  'per\\s+cent',
  'pct',
  'a\\.?m',
  'p\\.?m',
  "o['\\u2019]?clock",
  'minutes?',
  'mins?',
  'hours?',
  'hrs?',
  'days?',
  'weeks?',
  'wks?',
  'months?',
  'years?',
  'yrs?',
  'times?',
  COUNT_WORDS,
  'stars?',
  'square',
  'sq',
  'feet',
  'ft',
  'foot',
  'inch(?:es)?',
  'miles?',
  'steps?',
  'degrees?',
  'mph',
  'lbs?',
  'pounds?',
  'oz',
  'gallons?',
  'out\\s+of',
  'of\\s+(?:the|them|those|your|our)',
  MONTH_NAMES,
].join('|');

/** What follows a score that makes it a count of done things: "3 out of 5 rooms", "five out of five done". */
const NOT_A_COUNT =
  '(?!\\s+(?:(?:' +
  COUNT_WORDS +
  ')\\b|done\\b|finished\\b|complete(?:d)?\\b|through\\b|left\\b|to\\s+go\\b|of\\s+(?:the|them|those|your|our)\\b))';

/** A unit: a sign that is one (a percent, a degree), or a unit word whole. */
const UNIT = '(?:%|\\u00b0|(?:' + UNIT_WORDS + ')\\b)';

/** An optional second figure that makes the first a range, before the unit is looked for. */
const RANGE = '(?:\\s*(?:-|\\u2013|\\u2014|to|or|and)\\s*\\d+)?';

/** An optional word between the figure and its unit: "20 more minutes". */
const MORE = '(?:\\s+(?:more|extra|additional|other))?';

/** The lookahead every figure shape ends with: not the front of a longer figure, and no unit after. */
const NOT_UNIT = '(?![.,]\\d)(?!' + RANGE + MORE + '\\s*' + UNIT + ')';

/** The words that end a street address. */
export const STREET_SUFFIXES =
  'st|street|ave|avenue|blvd|boulevard|dr|drive|rd|road|ln|lane|ct|court|cir|circle|way|pl|place|ter|terrace|hwy|highway|pkwy|parkway|trail|trl|loop|unit|apt|suite|ste';

/** Not an address: no street suffix within the three words after the figure. */
const STREET = '(?!\\s+(?:[A-Za-z]+\\s+){0,2}(?:' + STREET_SUFFIXES + ')\\b)';

/**
 * A rate somebody else charges, named earlier in the clause: "the garage at your building charges 5
 * an hour". A negative lookbehind, so a money verb after it is not ours.
 */
const THIRD_PARTY_RATE =
  '(?<!\\b(?:garage|garages|lot|meter|meters|valet|parking|toll|tolls|deck|kiosk)\\b[^.!?\\n;,]{0,30}\\s)';

/** The kinds of fee a text may say there is none of, as in "no cancellation or late fee". */
const FEE_KINDS =
  'cancellation|cancel|late|card|credit\\s+card|processing|convenience|service|trip|travel|booking|fuel|rush|pet|no[\\s-]show|holiday|weekend|after[\\s-]hours|key|lockout|change|rescheduling|reschedule|hidden|extra';

/**
 * A fee or a penalty said not to exist, which is the truth and never refuses: "no fee", "no
 * cancellation or late fee", "we don't charge a cancellation fee", "without any penalty for". A
 * negative lookbehind: a negation, then only the words that carry it to the fee.
 */
const NO_FEE =
  "(?<!(?:\\bno|\\bwithout|\\bnever|\\bnot|\\bzero|n['\\u2019]t)(?:\\s+(?:any|a|an|the|ever|charge|charges|charging|charged|you|her|him|them|have|has|had|be|is|are|will|apply|applies|other|such|kind\\s+of|(?:" +
  FEE_KINDS +
  ')\\s+(?:or|and|nor)|(?:' +
  FEE_KINDS +
  '),))*\\s+(?:credit\\s+)?)';

/**
 * A fee somebody else charges, said as theirs: "his lockout fee", "the HOA's gate fee". Never
 * "Faithfully Kept's".
 */
const SOMEBODY_ELSES =
  "(?<!\\b(?:his|her|their|its)\\s+)(?<!\\b(?!kept['\\u2019])[a-z]+['\\u2019]s\\s+)";

/** How she pays, "check" only as the thing she writes ("by check", "a check"), never the verb. */
const PAY_METHOD =
  'zelle|cash|cards?|credit\\s+card|debit|(?<=\\b(?:by|a|your|with|paper|personal|the)\\s)checks?|cheques?|ach|venmo|paypal|autopay|bank\\s+transfer';

/** A guest who is a person, never a room named for one: "the guest bath", "the guest room". */
const NOT_A_GUEST_ROOM =
  '(?!(?<=guests?)\\s+(?:bath|bathroom|bedroom|room|suite|house|closet|towel|bed|wing|side|toilet|shower|cottage|quarters|area)s?\\b)';

/**
 * The business as who does it, before a verb of brokerage: "we can show", "we'll list", "would you
 * like us to list", "we're happy to show", "we would be glad to list".
 */
const BY_US =
  "\\b(?:(?:we|i)(?:\\s+(?:can|could|will|would|also|are|am|be))*|(?:we['\\u2019](?:ll|re|d)|i['\\u2019](?:ll|m|d))(?:\\s+(?:also|be))*|us\\s+to)(?:\\s+(?:happy|glad|able|willing)\\s+to)?\\s+";

/** A time something took, said before the figure: "both bathrooms took about 40 each". */
const DURATION_BEFORE =
  '(?<!\\b(?:took|takes|take|taking|ran|runs|run|running|lasted|lasts|last|spent|spend|spends|needed|needs|need)\\s+(?:about\\s+|around\\s+|roughly\\s+|maybe\\s+|nearly\\s+|almost\\s+|over\\s+|under\\s+|just\\s+|only\\s+|another\\s+)?)';

/**
 * Cash found in her home, never a price: "we found 20 dollars under the couch cushion". A negative
 * lookbehind before a figure said with a money word; a currency mark stays a price everywhere.
 */
const FOUND_CASH =
  '(?<!\\b(?:found|find|finds|finding)\\s+(?:a\\s+|an\\s+|the\\s+|your\\s+|her\\s+|his\\s+|about\\s+|around\\s+|nearly\\s+|almost\\s+|over\\s+|some\\s+|another\\s+)?)';

/**
 * A banknote or a coin found in her home, never a price, wherever "found" stands: "a five dollar bill
 * under the bed", "a few dollar coins in the vacuum bin". Only a note or a coin that exists, a figure
 * standing alone, so "your 212 dollar bill" and "your two hundred dollar bill" stay a price. A negative
 * lookahead after the money word, looking back at the figure before it.
 */
const CASH_NOTE =
  '(?!(?<=(?:(?<![\\d,.])(?:1|2|5|10|20|50|100)|(?<!\\b(?:two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety|hundred|thousand)[\\s-]+)\\b(?:a|an|one|two|five|ten|twenty|fifty|hundred|few|couple))[\\s-]*dollars?)[\\s-]+(?:bills?|coins?)\\b)';

/**
 * A cover laid earlier in the sentence, so "covered" is the sheet's: "we put sheets over the
 * dresser, so your things are covered while we dust the fan". Never when what follows makes it a
 * promise again: "we put sheets over the couch, and your things are covered if anything breaks".
 */
const COVER_LAID =
  '(?:(?<!\\b(?:sheets?|tarps?|drop[\\s-]?cloths?|plastic|towels?|blankets?|covers?|cloths?)\\s+(?:over|on|across|onto|under|around)\\b[^.!?\\n]{0,80})|(?=\\s+(?:if|in\\s+case|for\\s+(?:any|all))\\b))';

/**
 * A figure that boasts of scale, never a cleaner's own day: over or more than any figure, a figure
 * with a plus, hundreds or thousands of. `SCALE_TEN` adds a bare figure of 10 or more, `SCALE_HUNDRED`
 * one of 100 or more, so "she cleaned 3 homes before yours" and "4 cleans completed" stay ordinary.
 */
const SCALE =
  '(?:over|more\\s+than|nearly|almost|well\\s+over|close\\s+to|upwards\\s+of)\\s+(?:\\d[\\d,]*\\+?|a\\s+(?:hundred|thousand))|\\d{1,3}(?:,\\d{3})+\\+?|\\d+\\+|hundreds\\s+of|thousands\\s+of';
const SCALE_TEN = '(?:' + SCALE + '|[1-9]\\d+|dozens\\s+of|countless|so\\s+many)';
const SCALE_HUNDRED = '(?:' + SCALE + '|[1-9]\\d{2,})';

/** A word between "our" and staff that is the classification, or the work: "our full-time staff". */
const STAFF_WORD =
  'full[\\s-]time|part[\\s-]time|salaried|hourly|paid|permanent|in[\\s-]house|own|trained|professional|cleaning|uniformed|experienced|dedicated|new|w[\\s-]?2|1099';

/** An opening on the schedule, or us named, which a word passed on can carry to people at large. */
const OPENING =
  'openings?|opened\\s+up|opening\\s+up|availability|open\\s+(?:slots?|spots?|days?|mornings?|afternoons?)|(?:slots?|spots?)\\s+(?:open|left|available)|room\\s+for\\s+(?:new|more|a\\s+few|one\\s+more)|(?:taking|accepting)\\s+(?:on\\s+)?new|new\\s+clients?|about\\s+us|our\\s+(?:name|number|website|card)';

/**
 * After "pass the word": nothing more, or people at large, or what the word is, never one person named
 * ("to Mark"). A lookahead, so the phrase found is "pass the word".
 */
const WORD_PASSED_ON =
  '(?=(?:\\s+(?:along|around|on))?(?:\\s*(?:[,.!?;:]|$)|\\s+that\\b|\\s+to\\s+(?:your\\s+|any\\s+|all\\s+(?:of\\s+)?(?:your\\s+)?|the\\s+)?(?:friends|neighbou?rs|co-?workers|colleagues|everyone|everybody|anyone|people|others|family\\s+and\\s+friends|building|street|block|community|neighbou?rhood|club|church)\\b))';

/** The words that lead into a figure after a money word: "costs only 150", "pay another 50". */
const LEAD =
  '(?:only\\s+|just\\s+|about\\s+|around\\s+|roughly\\s+|the\\s+|an?\\s+|another\\s+|an\\s+extra\\s+|an\\s+additional\\s+)?';

/** One rule of ops's own: every anchor matched on its own, and the one source they make together. */
export interface AnchoredRule extends ClaimsRule {
  readonly anchors: readonly string[];
}

/** One of ops's own refusals, under the code it refuses with. */
export interface OpsClaimsRule extends AnchoredRule {
  readonly code: ClaimsCode;
}

const refusal = (
  id: string,
  code: ClaimsCode,
  anchors: readonly string[],
  say: string,
): OpsClaimsRule => ({ id, code, anchors, source: anchors.join('|'), flags: 'i', say });

const amber = (id: string, anchors: readonly string[], say: string): AnchoredRule => ({
  id,
  anchors,
  source: anchors.join('|'),
  flags: 'i',
  say,
});

/**
 * OPS'S OWN REFUSALS, the rule table of 2026-10-16. The server refuses only what it can recognise with
 * high precision, anchored on the nouns and phrases the business's document forbids; a generic word
 * never refuses here, it warns on the screen (`OPS_WARNINGS`). Each anchor is matched on its own, so
 * a finding names every shape it stands on, and `claims.ts` drops one a longer finding of the same
 * rule already holds. `amount` is read to cents, so an invoice's own total or balance passes by value.
 */
export const OPS_RULES: readonly OpsClaimsRule[] = [
  refusal(
    'amount',
    'price_not_allowed',
    [
      // A mark before, a mark after, a currency code: a price wherever it stands.
      MARK + '\\s?' + NUM,
      '\\b' + NUM + '\\s?' + MARK,
      '\\b(?:usd|us\\$|aud|cad|eur|gbp)\\s?' + NUM + '\\b',
      // A money word after, and a spelled amount, never cash found ("we found 20 dollars under the
      // couch cushion", "a five dollar bill under the bed") nor a store's name ("a Dollar General run").
      FOUND_CASH + '\\b' + NUM + '\\s?(?:dollars?|bucks|usd|euros?|cents?)\\b' + CASH_NOTE,
      FOUND_CASH +
        '\\b(?:a|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety|hundred|thousand|few|couple)\\b(?:[\\s-]+(?:one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety|hundred|thousand|of|and|a))*[\\s-]+(?:dollars?|bucks)\\b(?!\\s+(?:general|tree|stores?|shops?)\\b)' +
        CASH_NOTE,
      // Two-place cents, less a possible time of day (the screen asks for 10:30) and a measure
      // ("4.75 hours", "3.75 inches").
      '\\b(?!(?:[1-9]|1[0-2])\\.[0-5]\\d\\b)\\d[\\d,]*\\.\\d{2}\\b' + NOT_UNIT,
      // A money verb, then the figure; never a rate a parking garage or a meter charges.
      THIRD_PARTY_RATE +
        '\\b(?:costs?|costing|priced\\s+at|charg(?:e|es|ed|ing)(?:\\s+(?:you|her|him|them|us|it))?|bill(?:s|ed|ing)?(?:\\s+(?:you|her|him|them))?|owes?|owed|owing|pay|pays|paid|paying|totals?|totall?ed|totall?ing|refund(?:s|ed|ing)?(?:\\s+(?:you|her|him|them))?)\\s+' +
        LEAD +
        MARK +
        '?\\s?' +
        NUM +
        '\\b' +
        NOT_UNIT +
        STREET,
      // "Comes to" a figure is a sum only when a sum comes to it: "that comes to 212", "the two cleans
      // come to 424", never "she comes to 3 houses before yours" or "Maria comes to 27 Sunset Cove".
      '(?<=\\b(?:it|that|this|which|what|all|everything|altogether|together|total|subtotal|bill|invoice|balance|price|cost|amount|sum|clean|cleans|visit|visits|oven|fridge|windows|add-?ons?|extras?)\\s+(?:all\\s+|then\\s+|now\\s+|only\\s+)?)(?:comes?|came)\\s+to\\s+' +
        LEAD +
        MARK +
        '?\\s?' +
        NUM +
        '\\b' +
        NOT_UNIT +
        STREET,
      // A money noun, then the figure.
      "\\b(?:total|subtotal|balance|price|prices|pricing|rate|rates|cost|costs|charge|charges|refund|amount|sum|fee|fees|deposit)\\s*(?:(?:is|are|was|were|will\\s+be|would\\s+be|of|for|comes?\\s+to|came\\s+to|at|=|:)\\s*|['\\u2019]s\\s+)(?:only\\s+|just\\s+|about\\s+|around\\s+|roughly\\s+|the\\s+same\\s+)?" +
        MARK +
        '?\\s?' +
        NUM +
        '\\b' +
        NOT_UNIT +
        STREET,
      // An invoice or a bill, then the figure; never what it is for or its number ("your invoice for
      // 27 Sunset Cove", "Invoice: 1042"), which a street suffix cannot always tell from a sum.
      "\\b(?:bill|invoice)\\s*(?:(?:is|are|was|were|will\\s+be|would\\s+be|of|comes?\\s+to|came\\s+to|=)\\s*|['\\u2019]s\\s+)(?:only\\s+|just\\s+|about\\s+|around\\s+|roughly\\s+|the\\s+same\\s+)?" +
        MARK +
        '?\\s?' +
        NUM +
        '\\b' +
        NOT_UNIT +
        STREET,
      // A price tail, the figure 13 or more, never a time something took ("took about 40 each").
      DURATION_BEFORE +
        '\\b(?:\\d{1,3}(?:,\\d{3})+|[1-9]\\d{2,}|1[3-9]|[2-9]\\d)(?:\\.\\d+)?\\b(?![.,]\\d)\\s*(?:all\\s+in|each|apiece|per\\s+(?:hour|hr|visit|clean|cleaning|room|bathroom|bedroom|week|month|session|time)|an?\\s+(?:visit|clean|cleaning|session|month|week)|\\/\\s?(?:visit|clean|cleaning|hour|hr|week|wk|month|mo|room|session|time))\\b',
      // A total, the figure 100 or more: "212 total" is a sum, "the windows, 22 in total" a count.
      DURATION_BEFORE +
        '\\b(?:\\d{1,3}(?:,\\d{3})+|[1-9]\\d{2,})(?:\\.\\d+)?\\b(?![.,]\\d)\\s*(?:total|in\\s+total)\\b',
      // A tax tail, any figure.
      '\\b' +
        NUM +
        '\\b(?![.,]\\d)\\s*(?:plus\\s+tax|\\+\\s?tax|incl(?:uding|\\.)?\\s+tax|tax\\s+included|before\\s+tax|after\\s+tax)\\b',
    ],
    "Prices are never typed into a text; every rate is on https://faithfullykept.com/pricing/, so point them there. On an invoice message only the invoice's own balance or total may be named, and on a change to their standing cleans only their price of one clean under it.",
  ),
  refusal(
    'cut',
    'price_not_allowed',
    [
      // Never a store's name: "Maria is at Discount Tire on US 19".
      '\\bdiscount(?:s|ed|ing)?\\b(?!\\s+(?:tires?|auto|drugs?|stores?|mart|outlet|furniture|liquors?|depot|warehouse)\\b)',
      '\\bcoupons?\\b',
      '\\bpromo(?:s|tion|tions|tional)?\\b|\\bpromo\\s+code\\b',
      '\\bspecial\\s+(?:offers?|rates?|prices?|pricing|deals?|discounts?)\\b|\\bour\\s+specials\\b',
      '\\b\\d+\\s?(?:%|percent|per\\s+cent|pct)\\s?off\\b',
      '\\bhalf[\\s-](?:price|off)\\b',
      '\\bwaiv(?:e|ed|es|ing)\\b',
      // Free, never the house itself: "on the house cleaning price" (client-messages 1.8, ops #86).
      '\\bon\\s+the\\s+house\\b(?!\\s+(?:cleaning|watch)\\b)',
      // Knocked off a figure or a price, never "the wind knocked off a screen" or "we knocked off at noon".
      '\\bknock(?:ed|s|ing)?\\s+(?:' +
        MARK +
        '?' +
        NUM +
        '\\s*(?:%|percent|dollars?|bucks)?\\s+off|(?:it|that|this|a\\s+(?:bit|little))\\s+off\\s+(?:the|your)\\s+(?:price|cost|rate|bill|invoice|total|balance)|off\\s+(?:' +
        MARK +
        '?' +
        NUM +
        '|(?:a\\s+(?:bit|little)\\s+)?(?:the|your)\\s+(?:price|cost|rate|bill|invoice|total|balance)))\\b',
      '\\bmoney\\s+off\\b|\\bprice\\s+(?:cut|drop|match|reduction)s?\\b|\\brate\\s+reductions?\\b|\\bbeat\\s+(?:any|their|the|your)\\s+(?:price|quote|rate)\\b',
      '\\b(?:take|takes|took|taking|shave|shaves|shaved)\\s+' +
        MARK +
        '?' +
        NUM +
        '\\s*(?:%|percent|dollars?|bucks)?\\s+off\\b',
      '\\bdeduct(?:s|ed|ing|ions?)?\\b',
      '\\bsav(?:e|es|ed|ing)\\s+(?:you\\s+|her\\s+|him\\s+|them\\s+)?' +
        MARK +
        '?' +
        NUM +
        '\\b' +
        NOT_UNIT,
      // A share saved is a cut, though a share is a unit to every figure: "pay with Zelle and save 5%".
      '\\bsav(?:e|es|ed|ing)\\s+(?:you\\s+|her\\s+|him\\s+|them\\s+)?(?:an?\\s+(?:extra\\s+|additional\\s+)?)?\\d+(?:\\.\\d+)?\\s?(?:%|percent\\b|per\\s+cent\\b|pct\\b)(?!\\s+of\\b)',
      '\\b(?:give|gives|gave|giving|send|sends|sent|pay|pays|paid)\\s+(?:you|her|him|them)\\s+' +
        MARK +
        '?' +
        NUM +
        '\\s+back\\b',
      // Not "sale": a store's sale price is somebody else's.
      '\\b(?:reduced|lower|lowered|discounted|introductory|intro|loyalty|better|cheaper)\\s+(?:price|prices|pricing|rate|rates|deal|deals)\\b',
      '\\bcomplimentary\\b|\\bbuy\\s+one\\s+(?:clean|cleaning|visit|deep\\s+clean),?\\s+get\\s+one\\b',
      // Buy one, get one, beside a service, never a store's: "Publix has a BOGO on paper towels".
      '\\b(?:bogo|buy\\s+one,?\\s+get\\s+one)\\b(?=[^.!?\\n]{0,40}\\b(?:clean|cleans|cleaning|visit|visits|oven|fridge|windows?|add-?ons?|extras?|re-?clean)\\b)|(?<=\\b(?:clean|cleans|cleaning|visit|visits|oven|fridge|windows?|add-?ons?|extras?|re-?clean)\\b[^.!?\\n]{0,40})\\b(?:bogo|buy\\s+one,?\\s+get\\s+one)\\b',
      "\\b(?:oven|fridge|windows?|clean|cleaning|visit|re-?clean|add-?ons?|extras?|first\\s+one|next\\s+one)(?:\\s+(?:is|are|was|will\\s+be)|['\\u2019]s)\\s+on\\s+us\\b",
      // Never the laundry: "we threw in your cleaning rags with the towels".
      '\\b(?:throw|throws|threw|throwing|thrown)\\s+in\\s+(?:the\\s+|a\\s+|an\\s+|your\\s+)?(?:oven|fridge|windows?|cleaning|clean|re-?clean|extras?|add-?ons?|baseboards|blinds|cabinets)\\b(?!\\s+(?:rags?|cloths?|towels?|mitts?|racks?|supplies|products?|caddy|caddies|buckets?|mops?|kits?|sprays?|solution|wipes|gloves|brush(?:es)?|sponges?|liners?|filters?|soap|loads?|cleaners?)\\b)',
    ],
    'There is no price cut or coupon in this business, so there is nowhere to record one. Take it out; what they pay is the rate on the card.',
  ),
  refusal(
    'credit',
    'price_not_allowed',
    [
      '\\b(?:a|an|the|your|some|any|this|that)\\s+(?:' +
        MARK +
        '?' +
        NUM +
        '\\s+)?credits?\\s+(?:on|toward|towards|against|for|to)\\s+(?:your\\s+|the\\s+|a\\s+|her\\s+)?(?:next\\s+|future\\s+|following\\s+|upcoming\\s+|last\\s+)?(?:visit|clean|cleaning|invoice|bill|balance|account|month|week)\\b',
      '\\bcredit(?:ed|s|ing)?\\s+(?:your|the|her)\\s+(?:account|invoice|balance|card\\s+back)\\b',
      '\\b(?:account|invoice|balance|store|service)\\s+credits?\\b',
      '\\bcredited?\\s+(?:back|to\\s+your)\\b',
      '\\b(?:apply|applied|applying|issue|issued|issuing)\\s+(?:you\\s+|her\\s+)?(?:a\\s+|an\\s+|the\\s+)?(?:' +
        MARK +
        '?' +
        NUM +
        '\\s+)?credits?\\b',
      // "Give her a credit" is a cut; "I give her credit, the grout looks new" is praise.
      '\\b(?:give|gave|given|giving)\\s+(?:you\\s+|her\\s+)?(?:a\\s+|an\\s+|the\\s+|' +
        MARK +
        '?' +
        NUM +
        '\\s+)(?:' +
        MARK +
        '?' +
        NUM +
        '\\s+)?credits?\\b',
    ],
    'A credit on a future visit is a price cut. Take it out; a re-clean is the promise, at no charge.',
  ),
  refusal(
    'free',
    'price_not_allowed',
    [
      '\\bfor\\s+free\\b',
      '\\bfree\\s+of\\s+charge\\b',
      // Never a time somebody is free: "a free window Friday", "a free hour", "free this time next week".
      '\\bfree\\s+(?:clean|cleaning|visit|re-?clean|deep\\s+clean|oven|fridge|add-?ons?|service|extra|upgrade|quote|estimate|consultation|trial|first\\s+(?:clean|visit))\\b',
      // A service named free, with or without a verb between: "the fridge is free", "oven cleaning free".
      // Never a clean or a visit, which is a slot as often as a service: "the Friday clean is free now".
      "\\b(?:oven|fridge|windows|re-?clean|deep\\s+clean|add-?ons?|extras?|baseboards|blinds|cabinets|upgrade|(?:oven|fridge|window)\\s+clean(?:ing)?)(?:\\s+(?:is|are|was|will\\s+be|comes?)|['\\u2019]s)?\\s+(?:for\\s+)?free\\b(?!\\s+(?:of|up|to)\\b)",
      '\\b(?:do|does|did|doing|clean|cleans|cleaned|add|adds|added|include|includes|included|throw\\s+in)\\s+(?:the|your|it|that|them|this)\\s+(?:[a-z]+\\s+)?free\\b(?!\\s+(?:of|up|to)\\b)',
      '\\bno\\s+(?:extra\\s+|additional\\s+)?(?:charge|cost|fee)\\s+for\\s+the\\s+(?:oven|fridge|windows?|add-?ons?|extras?|deep\\s+clean|baseboards|blinds|cabinets)\\b',
      // A service done at nothing, never the re-clean ("clean the oven again at no charge", "redo the
      // oven") nor a move that costs nothing ("we moved your oven add-on to Friday at no charge").
      '\\b(?:do|does|did|doing|clean|cleans|cleaned|cleaning|add|adds|added|adding|include|includes|included|including|throw\\s+in|throwing\\s+in)\\s+(?:the\\s+|your\\s+)?(?:inside\\s+(?:of\\s+)?(?:the\\s+|your\\s+)?)?(?:oven|fridge|refrigerator|windows?|add-?ons?|extras?|deep\\s+clean|baseboards|blinds|cabinets|(?:oven|fridge|window)\\s+clean(?:ing)?)(?:\\s+(?:too|as\\s+well|also|today|this\\s+time|for\\s+you))?\\s+(?:at|for)\\s+no\\s+(?:extra\\s+|additional\\s+)?(?:charge|cost)\\b',
    ],
    '"Free" prices something at nothing. Take it out; the re-clean promise is said as the website says it: tell us within 24 hours and we clean it again at no charge.',
  ),
  refusal(
    'credential',
    'claim_not_allowed',
    [
      '(?<!\\b(?:no|without)\\s+(?:general\\s+)?)\\bgeneral\\s+liability\\b',
      '(?<!\\b(?:no|without)\\s+(?:general\\s+)?)\\bliability\\s+(?:cover|coverage|insurance|policy|policies|protection|limit)\\b',
      // Coverage only as insurance ("full coverage", "coverage for damage"), never a shift covered ("we
      // have coverage Friday"); a licence never a licence plate.
      "\\b(?:we|we['\\u2019]ve|we['\\u2019]re|our\\s+(?:business|company)|faithfully\\s+kept|the\\s+business|the\\s+company)\\s+(?:also\\s+|now\\s+|both\\s+)?(?:carry|carries|have|has|hold|holds|got|are|is|maintain|maintains|offer|offers)\\s+(?:(?:a|an|the|our|full|general|complete|comprehensive|commercial|business|cleaning|janitorial|surety|liability|proper|real)\\s+)*(?:insurance|insurer|insurance\\s+policy|insurance\\s+coverage|coverage(?<=\\b(?:full|complete|comprehensive|commercial|general|liability|business|proper|real)\\s+coverage)|coverage\\s+for\\s+(?:damage|damages|breakage|breakages|loss|losses|accidents?|theft|anything|any\\s+damage)|cover\\s+for\\s+(?:damage|breakage|loss|accidents?|theft)|bond|bonding|licen[cs]e|licen[cs]es|certification|certifications|accreditation|accreditations|credentials?|membership|work(?:er|ers|man|men)?['\\u2019]?s?['\\u2019]?\\s*comp(?:ensation)?)\\b(?!\\s+(?:plates?|tags?)\\b)",
      // Never the area served: "inside our coverage area".
      '\\bour\\s+(?:insurance|insurer|policy\\s+number|bond|licen[cs]e\\s+number|licen[cs]e|certification|accreditation|credentials|membership|coverage(?!\\s+(?:area|areas|zone|zones|map|radius)\\b))\\b',
      // A number of one, holding at least one digit: "her license number and name" is not one.
      '\\b(?:insurance|coverage|policy|bond|licen[cs]e|certification)\\s+(?:number|no\\.?|#)\\s*(?=[A-Z-]*\\d)[A-Z0-9-]{3,}',
      "\\b(?:business|state|cleaning|contractor['\\u2019]?s?|occupational|county|city|professional)\\s+licen[cs]e\\b",
      '\\b(?:surety|janitorial|business|cleaning|employee\\s+dishonesty)\\s+bonds?\\b',
      '\\b(?:we|she|he|they|cleaners?|team|crew|everyone)\\s+(?:is|are|was|were|have|has|got|hold|holds)\\s+(?:all\\s+|fully\\s+|now\\s+)?certif(?:ied|ication)\\b',
      '\\bcertified\\s+(?:by|in|with|through|to)\\b',
      // The plurals the copied `accreditation` misses at its closing word boundary.
      '\\bcertified\\s+(?:cleaners|professionals)\\b',
      // Never her own homeowners, condo or neighborhood association: "a member of the condo association".
      "\\bmembers?\\s+of\\s+(?:the\\s+|an?\\s+)?(?!(?:[a-z&'.-]+\\s+){0,4}?(?:homeowners?['\\u2019]?|home\\s*owners?['\\u2019]?|condo(?:minium)?s?|hoa|neighbou?rhoods?|residents?['\\u2019]?|tenants?['\\u2019]?|owners?['\\u2019]?|community|civic|board|parents?['\\u2019]?|pta|alumni|garden|church|parish|tennis|golf|yacht|country)\\b)(?:[a-z&'.-]+\\s+){0,5}(?:association|alliance|chamber|guild|institute|federation|society|bureau|network|coalition|trade\\s+group)\\b",
      '\\b(?:bbb|better\\s+business\\s+bureau|issa|arcsi|iicrc|nhwa|angi|homeadvisor|thumbtack)\\s+(?:member|members|membership|accredited|certified|approved|verified|screened|rated|listed|pro)\\b',
      '\\b(?:member|membership|accredited|certified|approved|verified|listed|rated|screened)\\s+(?:with|by|of|on)\\s+(?:the\\s+)?(?:bbb|better\\s+business\\s+bureau|issa|arcsi|iicrc|nhwa|angi|homeadvisor|thumbtack|chamber\\s+of\\s+commerce)\\b',
      '\\bA\\+\\s*(?:rated|rating)\\b',
      "\\bwork(?:er|ers|man|men)?['\\u2019]?s?['\\u2019]?\\s*comp(?:ensation)?\\b",
    ],
    "The business holds no insurance, bond, licence, certification, membership or workers' compensation, so a text never names one as ours. If they ask, ring them and say so plainly.",
  ),
  refusal(
    'paraphrase',
    'claim_not_allowed',
    [
      '\\bpeace\\s+of\\s+mind\\b|\\brest\\s+easy\\b|\\bworry[\\s-]free\\b|\\bhassle[\\s-]free\\b(?=[^.!?\\n]{0,30}\\b(?:covered|protected|insured|bonded)\\b)',
      '\\bsafeguard(?:s|ed|ing)?\\b',
      // Said of her or her home, never of a shift ("Ana has it covered") or a chore ("we've got that covered").
      '\\b(?:got|have|has|had)\\s+(?:you|your\\s+(?:home|house|property|belongings|things|valuables|place))\\s+(?:all\\s+|fully\\s+|completely\\s+)?covered\\b',
      // Never what the weather or a sheet is kept off: "everything is covered for the storm", "your
      // house is protected on that side", "protected by the shutters", and a cover laid before it.
      // "With" is the sheet's, never "with us": "you're covered with us" is the promise.
      "\\b(?:you|your\\s+(?:home|house|property|belongings|things|valuables|keys?|place)|anything\\s+broken)(?:\\s+(?:is|are|will\\s+be|would\\s+be)|['\\u2019]s|['\\u2019]re)\\s+(?:all\\s+|fully\\s+|completely\\s+|totally\\s+|well\\s+|always\\s+)?(?:covered|protected|insured|safeguarded)\\b" +
        COVER_LAID +
        '(?!\\s+(?:in|with(?!\\s+(?:us|faithfully)\\b)|up|over|against|from\\s+(?:the\\s+)?(?:sun|rain|dust|storm|wind|weather|water|debris|heat|cold|paint|spray|scratches|bugs)|for\\s+(?:the\\s+)?(?:storm|rain|hurricane|winter|night|weekend|move|painters?)|on\\s+(?:that|this|one|both|each|the\\s+(?:north|south|east|west|front|back|pool|lanai|street|garage|other))\\s+sides?|by\\s+(?:the\\s+)?(?:shutters?|tarps?|plywood|sheets?|plastic|towels?|mats?|blankets?|screens?))\\b)',
      '(?:\\b(?:fully|completely|totally)|\\b100\\s?%)\\s+(?:covered|protected|insured|bonded|licen[cs]ed)\\b(?!\\s+(?:in|with(?!\\s+(?:us|faithfully)\\b))\\b)',
      '\\b(?:covered|protected|insured)\\s+by\\s+(?:us|our|faithfully|the\\s+business|insurance|a\\s+policy|our\\s+policy)\\b',
      '\\b(?:damage|damages|breakage|breakages|loss|losses|accidents?|theft)\\s+(?:is|are|will\\s+be|would\\s+be)\\s+(?:all\\s+|fully\\s+|always\\s+)?(?:covered|protected|insured)\\b',
      '\\btrusted\\s+(?:by|name|cleaners?|cleaning|team|company|service|business|choice|local|provider|professionals?|house\\s+cleaners?)\\b|\\b(?:most|more|the\\s+most)\\s+trusted\\b',
      // Said of the business, never of a routine: "we will have an established routine".
      "\\b(?:well|long)[\\s-]established\\b|\\bestablished\\s+(?:business|company|cleaners?|cleaning|team|name|service|brand|reputation|local)\\b|\\b(?:we\\s+are|we['\\u2019]re)\\s+(?:an?\\s+)?established\\b",
      '\\bpre[\\s-]?screened\\b|\\bscreened\\s+(?:in\\s+advance|beforehand|before\\s+(?:she|he|they|anyone|starting))\\b',
    ],
    'If the sentence needs that word to work, it is making a promise the business cannot keep. Rewrite the sentence, do not swap the word.',
  ),
  refusal(
    'background_check',
    'claim_not_allowed',
    [
      '\\bbackground[\\s-]?(?:checks?|checking|screenings?|investigations?|reports?)\\b',
      // A records search by its maker; never the police report a break-in takes.
      '\\b(?:criminal|police|fbi|fdle|level[\\s-]?(?:1|2|one|two)|national|county|state)\\s+(?:background|records?|history)\\s+(?:checks?|screenings?|search(?:es)?|reports?)\\b',
      '\\b(?:criminal|fbi|fdle|level[\\s-]?(?:1|2|one|two))\\s+(?:checks?|screenings?|search(?:es)?|reports?)\\b|\\bpolice\\s+(?:checks?|screenings?|clearances?)\\b',
      '\\bcriminal\\s+(?:records?|history|background)\\b',
      // Taken, never the smudges wiped off a fridge.
      '\\bfingerprinted\\b|\\bfingerprinting\\b|\\bfingerprints?\\s+(?:checks?|checked|clearances?|cards?|scans?|screenings?|background)\\b',
      '\\bdrug[\\s-](?:tested|screened|test|screen)\\b',
      // Never the gate's security check: "she passed the security check at the gate".
      '\\bpassed\\s+(?:(?:a|the|her|his|their|our|all|every)\\s+)?(?:(?:background|criminal|police)\\s+)?(?:checks?|screenings?)\\b(?!\\s+(?:to|over|on|in|out|for|at|with|by)\\b)',
      // Never checked at a gate or checked twice: "she got checked at the gate", "they were checked twice".
      // "Has been" contracted too: "she's been screened", "every cleaner's been checked".
      "\\b(?:cleaners?|crew|team|staff|everyone|everybody|she|he|they|all\\s+of\\s+them|each\\s+of\\s+them|people\\s+we\\s+send)(?:\\s+(?:(?:has|have|had)\\s+(?:all\\s+)?been|is|are|was|were|get|gets|got)|['\\u2019](?:s|ve|d)\\s+(?:all\\s+)?been)\\s+(?:all\\s+|fully\\s+|carefully\\s+|properly\\s+|thoroughly\\s+)?(?:screened|checked|vetted|background[\\s-]checked)\\b(?![\\s-]+(?:in|out|into|on|off|for|over|at|twice|again|before|after|when|while|daily|every|each|by\\s+(?:the\\s+)?(?:vet|doctor|nurse|guard|gate|security|front\\s+desk|doorman|concierge|hoa|building))\\b)",
      // The business screening in the present tense, never calls or the lanai: "we screen everyone",
      // "we carefully vet every cleaner".
      '\\bwe(?:\\s+(?:always|carefully|thoroughly|personally|also|properly|fully|still|do))?\\s+(?:screen|vet|background[\\s-]check)\\s+(?:everyone|everybody|anyone|anybody|(?:(?:every|each|all|all\\s+of|our|the|any|new|potential|prospective)\\s+)*(?:cleaners?|hires?|applicants?|candidates?|staff|employees?|workers?|team\\s+members?|crew))\\b',
      "\\b(?:we|i)\\s+(?:have\\s+|had\\s+)?(?:checked|screened|ran|run)\\s+(?:her|his|their|everyone['\\u2019]?s|every\\s+cleaner['\\u2019]?s|the\\s+cleaner['\\u2019]?s)\\s+(?:background|record|records|history)\\b",
      // A person's background said to have been looked at: "her background was checked".
      "\\b(?:her|his|their|everyone['\\u2019]?s|every\\s+cleaner['\\u2019]?s|the\\s+cleaners?['\\u2019]?s?)\\s+backgrounds?\\s+(?:was|were|is|are|has\\s+been|have\\s+been)\\s+(?:all\\s+|fully\\s+|properly\\s+|thoroughly\\s+)?(?:checked|screened|run|verified|cleared)\\b",
    ],
    "A text to a client never names the check, as a status or as a plan. Said on a call, the true sentence is the document's: nobody has been through a background check yet, because we are new.",
  ),
  refusal(
    'history',
    'claim_not_allowed',
    [
      "\\b(?:\\d+\\+?|a|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|fifteen|twenty|twenty[\\s-]five|thirty|forty|fifty|many|several|a\\s+few|a\\s+couple\\s+of|a\\s+dozen|decades\\s+of)\\s+(?:\\+\\s*)?years?['\\u2019]?\\s+(?:of\\s+)?(?:experience|expertise|in\\s+(?:the\\s+)?business|in\\s+(?:the\\s+|this\\s+)?(?:industry|trade|field|area)|of\\s+(?:cleaning|service|serving|doing\\s+this)|cleaning|serving|behind\\s+us)\\b",
      // Experience pooled, which only a team claims: "decades of combined experience".
      '\\b(?:(?:\\d+\\+?|many|several|ten|fifteen|twenty|thirty|forty|fifty)\\s+years|decades|years|a\\s+decade|over\\s+a\\s+decade)\\s+of\\s+(?:combined|collective|joint|shared|pooled)\\s+(?:experience|expertise)\\b',
      // Experience with no figure, said of us, never of her plumber: "our team has decades of
      // experience", "we have years of experience".
      "\\b(?:we(?:\\s+(?:have|all\\s+have|bring|had))?|we['\\u2019]ve(?:\\s+got)?|(?:our|the)\\s+(?:team|cleaners?|crew|company|business|people|staff)\\s+(?:has|have|had|brings?|all\\s+have)|faithfully\\s+kept\\s+(?:has|brings)|(?:team|crew|cleaners?|company|business)\\s+with)\\s+(?:(?:over|more\\s+than|nearly|almost|well\\s+over)\\s+)?(?:decades|years|a\\s+decade)\\s+of\\s+(?:(?:professional|hands[\\s-]on|cleaning|industry|real|combined|collective|joint|shared|pooled)\\s+)*(?:experience|expertise)\\b",
      // Cleaning as work done, never a cleaning the oven had: "it had not had a cleaning for years".
      '\\b(?:(?<!\\b(?:a|an|the|its|their|his|her|no|any|deep|good|proper|real)\\s)cleaning|in\\s+business|doing\\s+this|serving|at\\s+this|been\\s+around)\\b[^.!?\\n]{0,25}\\bfor\\s+(?:over\\s+|more\\s+than\\s+|nearly\\s+|almost\\s+|well\\s+over\\s+)?(?:\\d+\\+?\\s*|a\\s+|one\\s+|two\\s+|three\\s+|four\\s+|five\\s+|six\\s+|seven\\s+|eight\\s+|nine\\s+|ten\\s+|fifteen\\s+|twenty\\s+|thirty\\s+|many\\s+|several\\s+|a\\s+few\\s+|a\\s+couple\\s+of\\s+)?(?:years?|decades?|ages|generations?)\\b',
      '\\b(?:est\\.?|estd\\.?|founded|family[\\s-]owned\\s+since|serving\\s+[a-z\\s]{3,30}\\s+since)\\s+(?:in\\s+)?(?:19|20)\\d{2}\\b',
      // Clients and customers are ours bare; homes, families and neighbors only with a word that makes
      // them ours, never "dozens of homes in East Lake lost power".
      '\\b(?:hundreds|thousands|dozens|\\d+\\+?|countless|so\\s+many)\\s+of\\s+(?:(?:happy|satisfied|loyal|delighted|repeat|regular)\\s+(?:clients|customers|families|homes|households|homeowners|neighbou?rs)|(?:local\\s+)?(?:clients|customers))\\b',
      '\\b(?:\\d+\\+?|hundreds\\s+of|many|countless|so\\s+many|lots\\s+of|plenty\\s+of)\\s+(?:happy|satisfied|loyal|delighted|repeat|regular|five[\\s-]star|5[\\s-]star)\\s+(?:clients|customers|families|homes|reviews|ratings)\\b',
      // The homes worked for: "we have cleaned for over 200 families", "serving hundreds of homes".
      '\\b(?:(?:cleaned|cleaning|worked|working)\\s+for|served|serving)\\s+(?:over\\s+|more\\s+than\\s+|nearly\\s+|almost\\s+|well\\s+over\\s+|about\\s+|around\\s+)?(?:\\d[\\d,]*\\+?|hundreds\\s+of|thousands\\s+of|dozens\\s+of|countless|so\\s+many)\\s+(?:happy\\s+|satisfied\\s+|local\\s+|different\\s+)?(?:clients|customers|families|homes|households|homeowners)\\b',
      // The homes cleaned, at a scale no cleaner's day reaches: "we've cleaned over 500 homes", never
      // "she cleaned 3 homes before yours".
      '\\b(?:cleaned|cleaning)\\s+' +
        SCALE_TEN +
        '\\s+(?:(?:happy|satisfied|local|different)\\s+)?(?:clients|customers|families|homes|houses|households|homeowners)\\b',
      // The cleans done, at a scale no invoice counts: "over 1,000 cleans completed", never "4 cleans
      // completed in October". Homes, families and clients served are the copied `scale`'s.
      '\\b' +
        SCALE_HUNDRED +
        '\\s+(?:cleans|cleanings|visits|jobs|houses|households|customers)\\s+(?:completed|done|finished|cleaned|served|delivered|performed|and\\s+counting)\\b',
      // Never a decoration: "the five star ornaments".
      '\\b(?:\\d+(?:\\.\\d)?|one|two|three|four|five)[\\s-]*stars?\\b(?![\\s-]*(?:ornaments?|stickers?|decals?|cut-?outs?|lights?|shapes?|magnets?|toppers?|garlands?|decorations?|cookies?|fruit|anise|jasmine|pillows?|prints?|patterns?)\\b)',
      // Never a measure a product is rated to: "a filter rated 99.97 percent".
      '\\b(?:rated|rating|ratings|score|scored)\\s+(?:\\d+(?:\\.\\d)?|five|four)\\b(?![.,]\\d)(?!\\s*(?:%|\\u00b0|(?:percent|degrees?|mph|psi|volts?|watts?|amps?|lbs?|pounds?|gallons?)\\b))',
      // "Highly rated" only of the business: a spray is highly rated.
      "\\b(?:top|highest|best|5[\\s-]star|five[\\s-]star)[\\s-]rated\\b|\\b(?:we\\s+are|we['\\u2019]re|faithfully\\s+kept\\s+is|our\\s+(?:business|company|cleaners?|team|service)\\s+(?:is|are))\\s+(?:very\\s+|so\\s+)?highly[\\s-]rated\\b|\\bhighly[\\s-]rated\\s+(?:cleaners?|cleaning|service|team|company|business|local|in\\s+(?:pinellas|palm\\s+harbor|east\\s+lake|dunedin|oldsmar|tarpon|clearwater|the\\s+area|town|tampa\\s+bay|florida))\\b",
      '\\b\\d+\\.\\d+\\s*(?:\\/|out\\s+of)\\s*(?:5|five|10|ten)\\b',
      // Never a count of rooms: "we're 3 out of 5 rooms in".
      "\\b(?:rated|rating|score|scored|we\\s+are|we['\\u2019]re|us)\\s+(?:\\d+(?:\\.\\d)?|one|two|three|four|five)\\s*(?:\\/|out\\s+of)\\s*(?:5|five|10|ten)\\b" +
        NOT_A_COUNT,
      '\\b(?:\\d+(?:\\.\\d)?|one|two|three|four|five)\\s*(?:\\/|out\\s+of)\\s*(?:5|five)\\s+stars?\\b',
      // A score, whatever follows, unless a count follows: "five out of five from every client".
      '\\bfive\\s+out\\s+of\\s+five\\b' + NOT_A_COUNT,
      '\\bour\\s+(?:google\\s+|yelp\\s+|star\\s+|online\\s+|average\\s+)?ratings?\\b|\\b(?:google|yelp|facebook|nextdoor|angi|thumbtack|bbb)\\s+(?:rating|ratings|score|stars)\\b',
      // Said of the business, never her orchids.
      "\\baward[\\s-]winning\\s+(?:local\\s+)?(?:cleaners?|cleaning|service|team|company|business|crew|maids?|housekeep\\w*)\\b|\\b(?:we\\s+are|we['\\u2019]re|faithfully\\s+kept\\s+is)\\s+(?:an?\\s+)?award[\\s-]winning\\b",
    ],
    'The business is new, with no rating and no years behind it. Say: "We are new. That is why everything is published."',
  ),
  refusal(
    'review_ask',
    'claim_not_allowed',
    [
      '\\b(?:leave|write|post|drop|give|share|submit|add)\\s+(?:us\\s+|me\\s+)?(?:a\\s+|an\\s+|your\\s+)?(?:quick\\s+|short\\s+|brief\\s+|honest\\s+|kind\\s+|5[\\s-]star\\s+|five[\\s-]star\\s+|positive\\s+|good\\s+|nice\\s+|google\\s+|yelp\\s+|online\\s+)?(?:reviews?|ratings?|testimonials?|stars)\\b',
      '\\breviews?\\s+(?:us|link|request|form|page|site|survey|on\\s+(?:google|yelp|facebook|nextdoor|angi|thumbtack|the\\s+web)|online)\\b',
      '\\b(?:google|yelp|facebook|nextdoor|angi|thumbtack|online|public)\\s+(?:reviews?|ratings?|stars?|testimonials?|recommendations?|feedback)\\b',
      // Feedback asked for in public, never the survey's own "tell us": "leave us feedback on Google".
      '\\bfeedback\\s+(?:on|at|via|to)\\s+(?:google|yelp|facebook|nextdoor|angi|thumbtack|instagram|social\\s+media|the\\s+web)\\b|\\bfeedback\\s+online\\b',
      '\\b(?:rating|review|testimonial|feedback)\\s+(?:form|link|request|page|site)\\b',
      '\\brate\\s+(?:us|me|your\\s+(?:clean|cleaning|visit|experience|cleaner)|the\\s+(?:clean|cleaning|visit|service|cleaner))\\b',
      '\\byour\\s+rating\\s+(?:on\\s+google|online|helps|means|would)\\b',
      // Never one we make of our own work: "Ana will stop by for a quick review of the clean".
      "(?<!\\b(?:for|do|does|did|doing|after|during|with)\\s)\\b(?:a|an|your|the)\\s+(?:quick\\s+|short\\s+|brief\\s+)?(?:ratings?|reviews?|testimonials?)\\s+(?:of|for|on|about)\\s+(?:the|your|our|this|today['\\u2019]?s)\\s+(?:clean|cleaning|visit|service|work|cleaner|experience)\\b",
      '\\b(?:text|message)\\s+(?:us|me)\\s+(?:back\\s+)?(?:with\\s+)?(?:a|an|your)\\s+(?:\\w+\\s+)?(?:ratings?|reviews?|testimonials?)\\b',
      '\\breply\\b[^.!?\\n]{0,25}\\bwith\\s+(?:a|an|your)\\s+(?:\\w+\\s+)?(?:ratings?|reviews?|testimonials?)\\b',
      '\\bsend\\s+(?:us|me)\\s+(?:a|an|your)\\s+(?:\\w+\\s+)?(?:ratings?|reviews?|testimonials?)\\b',
      '\\btestimonials?\\b|\\bstar\\s+ratings?\\b',
      // A score asked for, never a visit window: "Thursday's window is from 1 to 5".
      '\\bon\\s+a\\s+scale\\s+of\\b|\\b(?:rate|rating|score|grade|stars?)\\b[^.!?\\n]{0,40}\\bfrom\\s+1\\s+to\\s+(?:5|10)\\b|\\bfrom\\s+1\\s+to\\s+(?:5|10)\\s+(?:stars?|points?)\\b',
      // The same asked in other words, never the window or the kids' stars: "on a 1 to 5, how did we
      // do?", "how many stars would you give us?", never "on a 1 to 5 window".
      '\\bon\\s+a\\s+(?:1|one)\\s*(?:-|\\u2013|to)\\s*(?:5|five|10|ten)\\b(?=\\s*,|\\s+(?:scale|how)\\b)',
      '\\bhow\\s+many\\s+stars?\\s+(?:would|will|do|does|did|should|could|can|might)\\s+(?:you\\s+give\\s+(?:us|me|the\\s+(?:clean|cleaning|visit|service|cleaner|team))|we\\s+(?:get|deserve|earn))\\b',
      '\\b(?:say|write|post|share|put|leave)\\s+(?:something|a\\s+few\\s+(?:kind\\s+)?words|a\\s+word|a\\s+line|a\\s+little|a\\s+note|a\\s+comment)\\s+(?:nice\\s+|kind\\s+|good\\s+|positive\\s+)?about\\s+us\\b',
      '\\babout\\s+us\\s+(?:on|at)\\s+(?:google|facebook|yelp|nextdoor|instagram|angi|thumbtack|social\\s+media|the\\s+web)\\b|\\babout\\s+us\\s+online\\b',
      // Others told in public or about us, never others at home told to use the side door.
      '\\b(?:let\\s+others\\s+know|tell\\s+others)\\b(?=[^.!?\\n]{0,30}\\b(?:on|at|via)\\s+(?:google|yelp|facebook|nextdoor|instagram|angi|thumbtack|social\\s+media|the\\s+web)\\b|\\s+(?:about\\s+us|online|how\\s+(?:we|it|the\\s+clean|your\\s+clean)\\s+(?:did|went|was|were)|what\\s+you\\s+think)\\b)|\\bhelp\\s+others\\s+find\\s+us\\b',
    ],
    'A review is never asked for in a text. Ask in person on the day of the clean, or by email.',
  ),
  refusal(
    'referral_ask',
    'claim_not_allowed',
    [
      // Never somebody we refer her to: "we can refer someone who does".
      '\\brefer\\s+(?:us|a\\s+friend|your\\s+(?:friends?|neighbou?rs?|family)|(?:anyone|someone|somebody)\\b(?!\\s+(?:who\\s+(?:does|do|can|could|will|would|knows?)|to\\s+(?:you|her)|for\\s+(?:that|it|the|this|you|her))\\b))\\b|\\breferrals?\\b|\\breferral\\s+(?:bonus|credit|program)\\b',
      '\\brecommend(?:s|ed|ing)?\\s+us\\b',
      // Never her recommendation of a mop or a plumber: "we appreciate the recommendation of the new mop".
      '\\b(?:love|appreciate|welcome|grateful\\s+for|value|thankful\\s+for)\\s+(?:a\\s+|any\\s+|your\\s+)?(?:recommendations?|referrals?|mentions?|shout[\\s-]?outs?|word\\s+of\\s+mouth)\\b(?!\\s+(?:of|for|about)\\b)',
      // Never to her building's front desk or gate: "mention our name to the front desk".
      '\\bmention\\s+us\\b(?!\\s+(?:to|at)\\s+(?:the|your)\\s+(?:front\\s+desk|desk|guard|guards|gate|guard\\s+house|concierge|doorman|door\\s+staff|security|office|front\\s+office|building|hoa|management|leasing\\s+office)\\b)|\\bmention\\s+(?:our\\s+name|faithfully\\s+kept|my\\s+name)\\s+to\\s+(?:your\\s+|any\\s+|all\\s+(?:of\\s+)?(?:your\\s+)?)?(?:friends?|neighbou?rs?|family|everyone|everybody|people|others|anyone|co-?workers?|colleagues?)\\b',
      // Telling them about us or what we offer, never when we come: "tell your family we are coming at 9".
      '\\b(?:tell|telling|let|letting)\\s+(?:your\\s+|any\\s+|all\\s+(?:of\\s+)?(?:your\\s+)?)?(?:friends?|neighbou?rs?|family|everyone|everybody|people|others|anyone)\\s+(?:about\\s+us|know\\s+about\\s+us|about\\s+faithfully\\s+kept|we\\s+(?:are\\s+(?:taking|accepting|booking|available|looking\\s+for|hiring|new\\s+in)|clean\\s+(?:homes|houses)|exist|have\\s+(?:openings?|room|availability|space|slots?|spots?)))\\b',
      '\\bspread\\s+the\\s+word\\b|\\bword\\s+of\\s+mouth\\b',
      // The word passed to people at large beside an opening, never to her husband: "we just opened up
      // Tuesdays, so pass the word to your neighbors", never "pass the word to Mark that we come at 9".
      '(?:(?<=\\b(?:' +
        OPENING +
        ')\\b[^.!?\\n]{0,60})\\bpass\\s+the\\s+word\\b' +
        WORD_PASSED_ON +
        '|\\bpass\\s+the\\s+word\\b' +
        WORD_PASSED_ON +
        '(?=[^.!?\\n]{0,60}\\b(?:' +
        OPENING +
        ')\\b))',
      // Our name or number passed on, never "I will pass the details on to the cleaner".
      "\\b(?:pass|share|give)\\s+(?:our|faithfully\\s+kept['\\u2019]?s?)\\s+(?:name|number|card|details|info|link|website)\\s+(?:along\\b(?!\\s+to\\s+(?:the|her|his)\\b)|around\\b|on\\b(?!\\s+to\\b)|(?:on\\s+|along\\s+)?to\\s+(?:a|your|any|all\\s+(?:of\\s+)?(?:your\\s+)?)\\s*(?:friends?|neighbou?rs?|family|anyone|everyone|people)\\b)|\\b(?:pass|share|give)\\s+(?:my|the)\\s+(?:name|number|card|details|info|link|website)\\s+(?:(?:on|along)\\s+)?to\\s+(?:a|your|any|all\\s+(?:of\\s+)?(?:your\\s+)?)\\s*(?:friends?|neighbou?rs?|family|anyone|everyone|people)\\b",
      // Anyone, or any neighbors or friends, she knows: "know any neighbors who need a cleaner?".
      '\\bknow\\s+(?:of\\s+)?(?:anyone|anybody|someone|somebody|(?:any|some|other|a\\s+few|a)\\s+(?:neighbou?rs?|friends?|people|folks|families|co-?workers?|colleagues?|relatives?))\\s+(?:who|that|else\\s+who)\\s+(?:needs?|wants?|is\\s+looking\\s+for|are\\s+looking\\s+for|could\\s+use|might\\s+(?:need|want|like))\\s+(?:a\\s+)?(?:cleaner|cleaning|clean|house\\s+cleaning|help\\s+(?:with|around)|us)\\b',
      // The same with "have" or "got", a friend or a neighbor only, never ours: "have a friend who needs
      // a cleaner?", never "we have someone who wants a clean on Thursday".
      "(?<!\\b(?:we|i)\\s)(?<!\\b(?:we|i)['\\u2019]ve\\s)\\b(?:have|got)\\s+(?:any|some|other|a\\s+few|a)\\s+(?:neighbou?rs?|friends?|co-?workers?|colleagues?|relatives?|family\\s+members?)\\s+(?:who|that)\\s+(?:needs?|wants?|is\\s+looking\\s+for|are\\s+looking\\s+for|could\\s+use|might\\s+(?:need|want|like))\\s+(?:a\\s+)?(?:cleaner|cleaning|clean|house\\s+cleaning|help\\s+(?:with|around)|us)\\b",
      // Never somebody at the gate asking for us: "if anyone asks for us at the gate".
      '\\b(?:if|when)\\s+(?:a\\s+|any\\s+|your\\s+)?(?:friend|neighbou?r|anyone|someone|somebody|family\\s+member|co-?worker|colleague)\\s+(?:of\\s+yours\\s+)?(?:ever\\s+)?(?:(?:needs?|wants?|is\\s+looking\\s+for|could\\s+use|mentions?)\\s+(?:a\\s+)?(?:cleaner|cleaning|clean|house\\s+cleaning|our\\s+(?:help|number|name)|us)|asks?\\s+(?:about|for)\\s+(?:a\\s+)?(?:cleaner|cleaning|house\\s+cleaning|recommendation))\\b',
      // An opening offered to somebody else, never to her own household for her own move: "we have an
      // opening Thursday, before your family arrives", "if someone can let us in".
      "\\b(?:friends?|neighbou?rs?|(?:anyone|anybody|someone|somebody|people)\\s+(?:you\\s+know|else)|new\\s+clients?|co-?workers?|colleagues?)\\b[^.!?\\n]{0,40}\\bwe(?:\\s+(?:have|got|still\\s+have)|['\\u2019]ve\\s+got)\\s+(?:a\\s+few\\s+|a\\s+couple\\s+(?:of\\s+)?|some\\s+|an?\\s+|two\\s+|three\\s+|several\\s+|more\\s+|new\\s+|extra\\s+|plenty\\s+of\\s+|limited\\s+)?(?:openings?|open\\s+slots?|open\\s+spots?|availability|room\\s+for)\\b",
      "\\bwe(?:\\s+(?:have|got|still\\s+have)|['\\u2019]ve\\s+got)\\s+(?:a\\s+few\\s+|a\\s+couple\\s+(?:of\\s+)?|some\\s+|an?\\s+|two\\s+|three\\s+|several\\s+|more\\s+|new\\s+|extra\\s+|plenty\\s+of\\s+|limited\\s+)?(?:openings?|open\\s+slots?|open\\s+spots?|availability|room\\s+for)\\b[^.!?\\n]{0,40}\\b(?:friends?|neighbou?rs?|(?:anyone|anybody|someone|somebody|people)\\s+(?:you\\s+know|else)|new\\s+clients?|co-?workers?|colleagues?)\\b",
      '\\b(?:taking|accepting|welcoming)\\s+(?:on\\s+)?new\\s+(?:clients|customers)\\b|\\bnow\\s+booking\\b',
    ],
    "A referral is never asked for in a text, and nobody else's clean is offered in theirs. Take it out.",
  ),
  refusal(
    'people',
    'claim_not_allowed',
    [
      '\\bour\\s+(?:team\\s+members?|staff\\s+members?|employees?|workers?|crew\\s+members?|staffers?|hires?|payroll)\\b',
      // The same with a classification or a word for the work between, which the copied `our-staff`
      // misses: "our full-time staff", "our salaried employees", "our own cleaning staff".
      '\\bour\\s+(?:' +
        STAFF_WORD +
        ')(?:(?:\\s*,\\s*|\\s+and\\s+|\\s*&\\s*|\\s+)(?:' +
        STAFF_WORD +
        '))*\\s+(?:team\\s+members?|staff(?:\\s+members?)?|staffers?|employees?|workers?|crew\\s+members?|hires?)\\b',
      // Never somebody else's trade: "they are contractors for the pool".
      '\\b(?:cleaners?|she|he|they|everyone|the\\s+team|the\\s+crew|the\\s+girls|the\\s+ladies)\\s+(?:is|are|was|were|will\\s+be)\\s+(?:all\\s+)?(?:an?\\s+|our\\s+)?(?:employees?|employed\\s+by\\s+us|on\\s+(?:our\\s+|the\\s+)?payroll|(?:independent\\s+)?contractors?|subcontractors?|1099s?|w[\\s-]?2s?|full[\\s-]time|part[\\s-]time|salaried)\\b(?!\\s+(?:for\\s+(?!us\\b|faithfully\\b|the\\s+business\\b)|from\\b|with\\b|doing\\b|working\\b|fixing\\b|at\\b|on\\b|in\\b|hired\\s+by\\b|sent\\s+by\\b))',
      // The classification said as how they are paid or hired: "we pay all our cleaners as employees".
      '\\b(?:pay|pays|paid|hire|hires|hired|employ|employs|employed|treat|treats|keep|keeps|have|classify|classified)\\s+(?:all\\s+)?(?:of\\s+)?(?:our|the|every|each|all)\\s+(?:cleaners?|team|crew)\\s+(?:as|on)\\s+(?:an?\\s+)?(?:employees?|w[\\s-]?2s?|payroll|(?:independent\\s+)?contractors?|subcontractors?|1099s?)\\b',
      '\\b(?:employees?|contractors?)\\s*,?\\s+not\\s+(?:contractors?|employees?)\\b',
      // With a comma and "single" too: "Same cleaner, every single visit".
      '\\b(?:the\\s+)?same\\s+(?:cleaner|person|girl|lady|woman|team|crew|people)\\s*,?\\s+(?:every|each)\\s+(?:single\\s+)?(?:visit|time|week|clean|cleaning|fortnight|month)\\b',
      // A verb between, never "the same cleaner as every other week": "the same lady will come every
      // single week".
      "\\b(?:the\\s+)?same\\s+(?:cleaner|person|girl|lady|woman|team|crew|people)\\b(?!\\s+as\\b)(?:\\s+[a-z'\\u2019]+){1,5}?\\s+(?:every|each)\\s+(?:single\\s+)?(?:visit|time|week|clean|cleaning|fortnight|month)\\b",
      '\\b(?:every|each)\\s+(?:visit|time|week|clean)\\b[^.!?\\n]{0,10}\\b(?:with|by|from)\\s+the\\s+same\\s+(?:cleaner|person|team|crew)\\b',
      // Her and him only as who comes, never the dog kept in: "we always keep her in the laundry room".
      '\\balways\\s+(?:get|have|send|keep|be)\\s+the\\s+same\\s+(?:cleaner|person|team|crew|girl|lady)\\b|\\balways\\s+(?:get|have|send)\\s+(?:her|him)\\b(?!\\s+(?:in|out|on|off|inside|outside|away|shut|locked|up|down|busy|fed|walked|crated|leashed|back)\\b)',
      // One person named between "always" and "every visit", never a thing done each visit: "we always
      // keep the side gate shut each visit", "each visit we always have the alarm set".
      '\\balways\\s+(?:get|have|send|keep)\\s+(?!(?:the|a|an|your|her|his|their|our|its|it|them|this|that|those|these|some|any|all|to|up|on|in|out)\\b)[a-z]+,?\\s+(?:on\\s+|for\\s+|at\\s+)?(?:every|each)\\s+(?:single\\s+)?(?:visit|time|week|clean)\\b',
      '\\b(?:every|each)\\s+(?:visit|time|week|clean)\\b[^.!?\\n]{0,20}\\balways\\s+(?:get|have|send|keep)\\s+(?!(?:the|a|an|your|her|his|their|our|its|it|them|this|that|those|these|some|any|all|to|up|on|in|out)\\b)[a-z]+\\b(?!\\s+(?:in|on|off|out|inside|outside|shut|locked|set|open|closed|away)\\b)',
      '\\bno\\s+no[\\s-]?shows?\\b|\\bzero\\s+no[\\s-]?shows?\\b|\\bnever\\s+(?:miss|missed)\\s+(?:a\\s+)?(?:visit|clean|cleaning|appointment|day)\\b|\\bnever\\s+(?:(?:had|have\\s+had|once\\s+had|been)\\s+)?(?:a\\s+|one\\s+)?no[\\s-]?shows?\\b',
    ],
    'A text never says what the cleaner is to the business and never promises who comes. Say "the cleaner assigned to your home".',
  ),
  refusal(
    'ch475',
    'claim_not_allowed',
    [
      // A guest only as a person, never the guest bath or the guest room.
      "\\bmanage\\s+(?:your|the|a|her|his|their|our\\s+clients['\\u2019]?)\\s+(?:rental|rentals|property|properties|home|house|condo|unit|airbnb|vrbo|listing|tenants?|guests?|bookings?|turnovers?)\\b(?!\\s+in\\s+\\d)" +
        NOT_A_GUEST_ROOM,
      '\\bmanag(?:ing|ed|es|ement)\\s+(?:of\\s+)?(?:your|the|her|his|their)\\s+(?:rental|rentals|property|properties|airbnb|vrbo|tenants?|guests?)\\b' +
        NOT_A_GUEST_ROOM,
      '\\b(?:rental|airbnb|vrbo|short[\\s-]term\\s+rental|str|vacation\\s+rental|vacation\\s+home)\\s+(?:management|managers?|manage)\\b',
      '\\b(?:we|i)\\s+(?:can\\s+|could\\s+|will\\s+|would\\s+|also\\s+)?(?:manage|rent|lease|list|market)\\s+(?:your|the)\\s+(?:place|home|house|condo|unit|property)\\s+(?:for|while|when|to|out|on)\\b',
      '\\bcollect(?:s|ed|ing)?\\s+(?:the\\s+|your\\s+)?rent\\b|\\brent\\s+collection\\b',
      '\\bhold(?:s|ing)?\\s+(?:a|the|your|her|their)\\s+(?:security\\s+|damage\\s+|rental\\s+)?deposit\\b',
      // A renter's deposit collected, never a deposit somebody else takes for a part: "collect the
      // security deposit", "collect the deposit from your tenants".
      '\\bcollect(?:s|ed|ing)?\\s+(?:a|the|your|her|their|any)\\s+(?:(?:security|damage|rental|pet)\\s+deposits?\\b|deposits?\\s+from\\s+(?:your|the|her|their)\\s+(?:renters?|tenants?|guests?)\\b)',
      // Showing and listing by us, never by her or her realtor: "your realtor is showing the house to
      // buyers", "since you are listing the house for sale".
      BY_US +
        'show(?:s|ed|ing)?\\s+(?:the|your)\\s+(?:property|house|home|unit|condo|place)\\s+to\\s+(?:a\\s+|the\\s+|any\\s+)?(?:(?:prospective|potential|possible|interested|future|new)\\s+)?(?:buyers?|renters?|tenants?|prospects?|applicants?|guests?)\\b',
      BY_US +
        'list(?:s|ed|ing)?\\s+(?:your|the)\\s+(?:property|house|home|unit|condo|place)\\s+(?:for\\s+(?:rent|sale|lease)|on\\s+(?:airbnb|vrbo|zillow|the\\s+mls))\\b',
      '\\bguest\\s+check[\\s-]?ins?\\b|\\bcheck\\s+(?:in|out)\\s+(?:your\\s+|the\\s+)?(?:guests?|renters?|tenants?)\\b|\\bturnovers?\\s+(?:and|with)\\s+(?:guest\\s+)?check[\\s-]?ins?\\b',
      // The same with the guests between: "check your guests in".
      '\\bcheck(?:s|ed|ing)?\\s+(?:your|the|her|their|any)\\s+(?:guests?|renters?|tenants?)\\s+(?:in|out)\\b',
      "\\b(?:we|i)(?:\\s+(?:are|am|will\\s+be|can\\s+be|act\\s+as)|['\\u2019](?:re|m))\\s+(?:your\\s+|the\\s+)?(?:property\\s+managers?|landlord['\\u2019]?s?\\s+agent|rental\\s+agent|leasing\\s+agent)\\b",
      // A modal between too, never her realtor's: "we can rent out your condo", "we'll rent out".
      "\\bwe(?:['\\u2019](?:ll|d))?(?:\\s+(?:can|could|will|would|also|might|may))*\\s+(?:rent|lease)\\s+(?:your|out)\\b",
    ],
    'Managing a rental is brokerage under Florida law, and the business does not do it. Use the safe verbs: inspect, check, monitor, report, coordinate access, notify.',
  ),
  refusal(
    'pay_method',
    'claim_not_allowed',
    [
      // Never one said not to exist, nor somebody else's ("his lockout fee", "the HOA's gate fee").
      NO_FEE +
        SOMEBODY_ELSES +
        '\\b(?:surcharges?|convenience\\s+fees?|processing\\s+fees?|card\\s+fees?|credit\\s+card\\s+fees?|late\\s+fees?|cancellation\\s+fees?|cancel\\s+fees?|service\\s+fees?|trip\\s+fees?|travel\\s+fees?|booking\\s+fees?|fuel\\s+fees?|rush\\s+fees?|pet\\s+fees?|no[\\s-]show\\s+fees?|holiday\\s+fees?|weekend\\s+fees?|after[\\s-]hours\\s+fees?|key\\s+fees?|lockout\\s+fees?)\\b',
      NO_FEE + SOMEBODY_ELSES + '\\bpenalt(?:y|ies)\\s+(?:for|if|when|of|on)\\b',
      // A fee for how she pays: "there is a 3% fee for paying by card".
      NO_FEE +
        SOMEBODY_ELSES +
        '\\b(?:fees?|surcharges?)\\s+(?:for|when|if)\\s+(?:you\\s+)?(?:pay|pays|paying|use|using)\\s+(?:by\\s+|with\\s+|through\\s+|via\\s+)?(?:a\\s+|your\\s+|the\\s+)?(?:cards?|credit|debit|zelle|checks?|cheques?|venmo|paypal|autopay|ach|bank)\\b',
      '\\brebates?\\b',
      // Cheaper by how she pays, never a product cheaper by the gallon: "cheaper if you buy the refill".
      '\\bcheaper\\s+(?:to\\s+pay|than\\s+(?:paying\\s+(?:by\\s+|with\\s+)?)?(?:cards?|cash|zelle|checks?|cheques?|venmo|paypal|autopay)|(?:if|when)\\s+(?:you\\s+)?(?:pay|paying)|(?:by|with)\\s+(?:cards?|cash|zelle|checks?|cheques?|venmo|paypal|autopay|ach|bank))\\b',
      '\\b(?:' +
        PAY_METHOD +
        ')\\b[^.!?\\n]{0,30}(?:\\b(?:cheaper|costs?\\s+(?:more|less|extra)|more\\s+expensive|less\\s+expensive|an?\\s+extra\\s+charge)\\b|\\ban?\\s+extra\\s+\\d+\\s?%|\\b\\d+\\s?%\\s+(?:more|less|extra|off)\\b)',
      '(?:\\b(?:cheaper|costs?\\s+(?:more|less|extra)|more\\s+expensive|less\\s+expensive|an?\\s+extra\\s+charge)\\b|\\b\\d+\\s?%\\s+(?:more|less|extra)\\b)[^.!?\\n]{0,30}\\b(?:' +
        PAY_METHOD +
        ')\\b',
    ],
    'The price is the same however they pay, and there is no fee of any kind. Take out anything that makes it differ.',
  ),
];

/**
 * THE SCREEN'S AMBER, never a refusal and never read by the server: the shapes the server leaves
 * alone because the bare word is ordinary English, where the screen can afford to be eager. Shown with
 * its `say` as the office types, Send left enabled; `warningsIn` never draws one on words a refusal
 * already stands on. The code warning is a function, `looksLikeACode`, beside these six.
 */
export const OPS_WARNINGS: readonly AnchoredRule[] = [
  amber(
    'figure_without_a_mark',
    [
      "\\b(?:is|are|was|were|will\\s+be|['\\u2019]ll\\s+be|would\\s+be|['\\u2019]d\\s+be|['\\u2019]s|be\\s+(?:about|around|roughly)|comes?\\s+to|makes\\s+it|that['\\u2019]s|it['\\u2019]s)\\s+(?:only\\s+|just\\s+|about\\s+|around\\s+|roughly\\s+)?" +
        FIGURE +
        '\\b' +
        NOT_UNIT +
        STREET,
      '\\b(?:clean(?:ing|s)?|visit|oven|fridge|windows?|add-?ons?|rate|price|cost|charge|deep|hour|extra|more)\\b[^.!?\\n]{0,20}\\b' +
        FIGURE +
        '\\b' +
        NOT_UNIT +
        STREET,
      '\\b' +
        FIGURE +
        '\\b' +
        NOT_UNIT +
        STREET +
        '[^.!?\\n]{0,20}\\b(?:clean(?:ing|s)?|visit|oven|fridge|windows?|add-?ons?|rate|price|cost|charge|deep)\\b',
      '\\b(?:an?\\s+)?(?:extra|additional|another|plus|only|just)\\s+' +
        FIGURE +
        '\\b' +
        NOT_UNIT +
        STREET,
      '\\b(?:[1-9]|1[0-2])\\.[0-5]\\d\\b',
    ],
    'That figure reads as a price with no dollar sign. Prices are never typed here; if it is minutes, say minutes, and a time is written 10:30.',
  ),
  amber(
    'price_at_nothing',
    [
      '(?<![\\w-])(?<!\\bfeel\\s)free(?![\\w-])(?!\\s+(?:up|to|of\\s+(?:streaks|dust|lint|hair|grease|clutter)|at|on|after|before|from|until|till|by|around|tomorrow|today|tonight|this|next|then|now|all\\s+(?:day|morning|afternoon|week)|(?:mon|tues?|wed(?:nes)?|thu(?:rs)?|fri|sat(?:ur)?|sun)(?:day)?s?|mornings?|afternoons?|evenings?|weekends?|early|late|between|if|when|whenever|any|most)\\b)',
      '(?<!\\bgive\\s+(?:her|him|them|you)\\s)\\bcredits?\\b(?!\\s*(?:card|union|cards)s?\\b)(?!\\s*(?:or|and|\\/)\\s*debit\\b)',
      '\\b(?:at\\s+)?no\\s+(?:charge|cost)\\b(?<!again\\s+at\\s+no\\s+charge)(?<!again,?\\s+no\\s+charge)',
      // Not a road: "a crash on US 19".
      '\\bon\\s+us\\b(?!\\s*-?\\s*\\d)',
    ],
    "'free', 'credit', 'no charge' or 'on us' prices something at nothing. Fine when it says when somebody is free, that a move costs nothing, or the published re-clean promise; never for a service.",
  ),
  amber(
    'never_say_word',
    [
      '\\bcovered\\b(?!\\s+(?:in|with|up|over|the|your|her|his|their|all|every|it|them|parking|spot|garage|porch|patio|walkway|entrance|call)\\b)',
      '\\bprotected\\b(?!\\s+(?:the|your|her|his|their|all|every|it|them)\\b)',
      '\\btrusted\\b|\\bestablished\\b',
      '\\bscreened\\b(?![\\s-]+(?:in[\\s-]+)?(?:porch|lanai|patio|enclosure|room|door|window|pool)(?:e?s)?\\b)',
      '\\bsecure(?:d|ly)?\\b(?!\\s+(?:anything|the|your|it|them|all|every|everything|loose)\\b)',
      '(?<!\\b(?:cell|phone|signal|wifi|wi-fi|mobile|data|network)\\s)\\bcoverage\\b',
      '\\binsurance\\b|\\binsurers?\\b|\\bbonds?\\b|\\bbackgrounds?\\b',
      "\\blicen[cs]es?\\b(?<!\\bdriver(?:s|['\\u2019]s|s['\\u2019])?\\s(?:licen[cs]es?))(?!\\s+plates?\\b)",
      '\\bcertified\\b(?!\\s+(?:mail|check|cheque|copy|letter)\\b)',
      '\\bleasing\\b|\\bproperty\\s+manage(?:ment|rs?)\\b|\\btenants?\\b|\\brentals?\\b',
    ],
    "This word is on the business's never-say list. It is fine when it names somebody else or what was done (their building's office, a neighbor they trust, the furniture covered with a sheet); it is never said of us.",
  ),
  amber(
    'ask_word',
    [
      '(?<!\\b(?:to|will|can|could|should|under|your|my|our|the|this|that|pending)\\s)(?<!\\bfor\\s(?:your|my|our)\\s)\\breviews?\\b(?!\\s+(?:the|your|it|them|this|that|my|our|what|and|of)\\b)',
      '\\bratings?\\b|\\brecommend(?:s|ed|ing|ations?)?\\b|\\brefer(?:s|red|ring)?\\b',
      '\\b(?:\\d+|one|two|three|four|five|six|seven|eight|nine)\\s+out\\s+of\\s+(?:5|five|10|ten)\\b(?!\\s+(?!stars?\\b)[a-z])',
    ],
    'If this asks them for a review, a rating or a referral, it cannot go by text: ask in person on the day, or by email. A review of the invoice, or a plumber they recommended, is fine.',
  ),
  amber(
    'people_word',
    [
      '\\bsame\\s+(?:cleaner|person|girl|lady|woman|team|crew)\\b',
      '\\bno[\\s-]shows?\\b',
      '\\balways\\s+(?:get|have|send|keep|be\\s+with)\\b',
      '\\bstaff\\b|\\bemployees?\\b|\\bteam\\s+members?\\b|\\bpayroll\\b|\\b1099s?\\b',
      '(?<!\\b(?:a|the|your|her|his|their|any|our\\s+pool|the\\s+pool)\\s)\\bcontractors?\\b',
    ],
    "A text never says what the cleaner is to the business and never promises who comes next time. 'The cleaner assigned to your home' is the phrase.",
  ),
  amber(
    'promise_word',
    [
      '\\bguarantee[sd]?\\b|\\bpromise[sd]?\\b|\\b100\\s?%',
      '\\bwithin\\s+(?:the\\s+next\\s+)?(?!(?:24|twenty[\\s-]four)\\s+(?:hours?|hrs?)\\b)(?:(?:\\d+(?:\\s*(?:-|\\u2013|to)\\s*\\d+)?|an?|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|fifteen|twenty|thirty|forty[\\s-]five|forty|sixty|ninety|half\\s+an?|the|a\\s+couple\\s+of|a\\s+couple|a\\s+few|few|couple\\s+of)\\s+)?(?:business\\s+|working\\s+)?(?:minutes?|mins?|hours?|hrs?|days?|weeks?)\\b',
      '\\bnever\\s+(?:late|miss|cancel)\\b|\\balways\\s+on\\s+time\\b|\\bon\\s+time\\s+(?:every|each)\\b',
    ],
    'The one promise by the clock is the published one: tell us within 24 hours and we clean it again at no charge. An arrival estimate is fine; a guarantee is not.',
  ),
];

/**
 * On a text that holds a currency mark, an invoice text, every unmarked figure of 13 or more the
 * blanking left standing is `figure_without_a_mark`'s too: "$212.00 or 200 if you pay today".
 */
export const FIGURE_BESIDE_A_MARK = '\\b' + FIGURE + '\\b' + NOT_UNIT + STREET;
